import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Input,
  Pagination,
  Panel,
  Select,
  Stat,
  Tabs,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
  type TagTone,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import {
  DOCUMENT_CONFIDENCE_KEY,
  NATURE_OF_WORK,
  SOURCE_OF_INCOME,
  noCiraCheckOnRecord,
  splitPersonName,
  type IdentityOneMoreTry,
  type PageMeta,
} from '@dayon/contracts';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { CASE_PARAM, LINK_NAMES_NO_CHECK, namesAnIdentityCheck } from './case-link.ts';
import { CorrectedAfterRefusal } from './CorrectedAfterRefusal.tsx';
import { documentWords } from './document-words.ts';
import { IdentityPhotos } from './IdentityPhotos.tsx';
import {
  ALLOW_ONE_MORE_TRY,
  AllowOneMoreTryDialog,
  OneMoreTryAllowed,
  oneMoreTryToast,
  whyTheCaseIsClosed,
} from './OneMoreTry.tsx';
import { PersonName } from './PersonName.tsx';
import { RecordFactsList } from './RecordFacts.tsx';
import {
  REASON_HINT,
  RefusalNotice,
  reasonGroups,
  verdictToast,
  type RefusalReason,
  type VerdictAnswer,
} from './refusal-words.tsx';
import {
  DECIDED_WORDS,
  advisesRejecting,
  calendarDay,
  decisionWho,
  laterAnswerWords,
  momentWords,
  outcomeTone,
  photosGoneWords,
  sourceWords,
  type IdentityRecordFacts,
  type IdentitySource,
  type LaterAnswer,
  type RecordDecision,
} from './record-words.ts';

/**
 * Identity checks — the queue of cases a person has to decide.
 *
 * ── WHY THIS SCREEN EXISTS ─────────────────────────────────────────────────
 *
 * Every provider failure becomes REVIEW, by the operator's decision: real people
 * fail automated checks for innocent reasons — a worn card, bad light, a married
 * name — and nothing automated writes REFUSED. The consequence is a queue, and
 * until this screen there was nowhere to see it. Records accumulated while the
 * player's own screen told them, truthfully, that "someone is looking at it".
 *
 * ── WHAT A REVIEWER IS ACTUALLY DOING ──────────────────────────────────────
 *
 * Two different jobs, and the queue says which before they open it:
 *
 *   CONFIRMING A READING. The provider read the document and the reviewer checks
 *   it — usually because one field came back below the confidence threshold. CIRA
 *   caps Philippine IDs at 0.79, so this is the ordinary case rather than an edge
 *   one.
 *
 *   MAKING ONE. The provider could not read the card at all, so there is no name
 *   and no date of birth. Item 21 wants the name "as it appears in the ID
 *   presented", and here that is a person reading the ID and typing it. The
 *   approval dialog asks for exactly those two fields, and only then.
 *
 * ── WHAT THIS SCREEN IS CAREFUL ABOUT ──────────────────────────────────────
 *
 * OPENING A DOCUMENT IS RECORDED. Every image link is minted per request and
 * writes `player.identity_document_access` before the URL exists — "who looked at
 * the customer's passport" is a question with an answer. The screen says so, so a
 * reviewer knows their reading is attributable rather than discovering it later.
 *
 * THE LINK LIVES SIXTY SECONDS. A presigned URL over that bucket is a bearer
 * token with a photograph attached. The screen opens it immediately and does not
 * keep it.
 *
 * ── AND THE CHECKS ALREADY DECIDED ─────────────────────────────────────────
 *
 * Staging, 15 Oct: once a check was decided, nothing here could find it again — the queue
 * lists only what waits for a person — so "how was this player verified?" was answered by a
 * database query. The Decided tab lists verified and refused checks, newest decision first,
 * and each opens in the same case view, read-only: how it was decided at the top, what the
 * check found without the advice written for somebody about to decide, and no verdict buttons.
 *
 * THE DATA SUPPLY, in full:
 *   GET  /api/kyc/identity/queue                 the cases, oldest first
 *   GET  /api/kyc/identity/decided               the decided checks, newest first, paged
 *   GET  /api/kyc/identity/:id                   one case, with the read fields
 *   GET  /api/kyc/identity/:id/images/:kind      a signed, audited, 60s link
 *
 * THE WRITES, in full:
 *   POST /api/kyc/identity/:id/verdict           VERIFIED or REFUSED
 *   POST /api/kyc/identity/:id/one-more-try      support allows a refused player one more try
 */

/** Every case the waiting list holds, counted by the route beside the oldest 200 it lists. */
export interface QueueCounts {
  waiting: number;
  overThreeDays: number;
  nothingRead: number;
}

export interface QueueRow {
  id: string;
  playerRef: string;
  playerName: string;
  brandName: string | null;
  reviewNote: string | null;
  providerStatus: string | null;
  faceMatchScore: number | null;
  liveness: string | null;
  hasExtractedIdentity: boolean;
  /**
   * It cannot be approved: CIRA holds no check this platform can point to
   * (`noCiraCheckOnRecord`, computed by the route; the reference itself is never sent).
   */
  noCiraCheckOnRecord: boolean;
  imagesHeld: string[];
  recordedAt: string;
  waitingDays: number;
}

export interface CaseDetail {
  id: string;
  playerRef: string;
  playerName: string;
  brandName: string | null;
  /* The record's own types (@dayon/contracts), so a case is drawn as a record is
     (`factsOfCase`). */
  outcome: IdentityRecordFacts['outcome'];
  provider: string;
  providerRef: string | null;
  providerStatus: string | null;
  document: {
    fullName: string | null;
    dateOfBirth: string | null;
    placeOfBirth: string | null;
    currentAddress: string | null;
    nationality: string | null;
    documentType: string | null;
    documentNumber: string | null;
    documentExpiry: string | null;
    confidence: Record<string, number>;
  };
  biometric: { faceMatchScore: number | null; liveness: string | null };
  /** Everything the player declared — the other half of the comparison. */
  declared: {
    /**
     * The name THIS CHECK was sent, written out (a one-name player said so in words): the one the
     * player corrected after a refusal before it (`correction`), else the one given at sign-up,
     * when there is one; otherwise the account's display name, a handle. The name CIRA was sent and
     * compared. A correction made after the check is `laterCorrection`, not this.
     */
    fullName: string | null;
    /**
     * Whether the player gave a name at sign-up — rather than holding only the display name of an
     * account opened before sign-up asked.
     */
    nameGivenAtSignUp: boolean;
    /** The date of birth this check was sent: corrected after a refusal before it, else from sign-up. */
    dateOfBirth: string | null;
    /**
     * THE FIRST ENTRY BESIDE A CORRECTION — after a refusal because the details did not match, the
     * player may correct the name and date of birth on their account (sql/1023). The day
     * (`YYYY-MM-DD`, Manila) of the correction this check was sent, what it changed, and the name
     * and date as given at sign-up; null when this check was sent none, and `fullName` and
     * `dateOfBirth` are then the first entry.
     */
    correction: {
      correctedOn: string;
      nameChanged: boolean;
      /** The name given at sign-up, written out; null when none was asked at sign-up. */
      signUpName: string | null;
      dateOfBirthChanged: boolean;
      signUpDateOfBirth: string | null;
    } | null;
    /**
     * A CORRECTION MADE AFTER THIS CHECK: its day, and the name (written out) and the date the
     * account holds now, each null where it did not change what this check was sent. A later fact:
     * the check's findings, CIRA's answer and the date's verdict are about what it was sent. Null
     * when nothing since differs; absent from an API older than this console.
     */
    laterCorrection?: {
      correctedOn: string;
      fullName: string | null;
      dateOfBirth: string | null;
    } | null;
    email: string | null;
    phone: string | null;
    residenceCountry: string | null;
    natureOfWork: string | null;
    natureOfWorkDetail: string | null;
    sourceOfIncome: string | null;
    sourceOfIncomeDetail: string | null;
    employerOrBusiness: string | null;
    registeredOn: string | null;
  };
  /**
   * What the service CONCLUDED, as opposed to what it read.
   *
   * All three are null for a check a person did by hand. That is "not checked", not
   * "passed", and the screen must never render the two the same way — a reviewer who
   * reads an absent verdict as a clean one is the failure this whole panel exists to
   * prevent.
   */
  providerSaid: {
    /** The sanctions and PEP screening — sql/1002. All three null when none ran. */
    screening: {
      sanctionsHit: boolean | null;
      pepHit: boolean | null;
      passed: boolean | null;
    };
    nameMatchesDeclared: boolean | null;
    documentExpired: boolean | null;
    failedStep: string | null;
    /** CIRA's advice on this answer, verbatim — REJECTED comes with a sanctions match (sql/1016). */
    advice: string | null;
    /** What CIRA said after the check came to a person: its analyst's decision on a PEP match. */
    laterAnswer: LaterAnswer | null;
    /**
     * Whether CIRA had answered this check before that later answer: the heading over it says
     * "again" only then. Absent from an API older than this console, when it says neither.
     */
    answeredBefore?: boolean;
  };
  reviewNote: string | null;
  /** Who decided and when, with their note; null while the check is not decided. */
  decision: RecordDecision | null;
  /**
   * Whether the name and the date of birth under `document` are the service's reading or the
   * reviewer's typing: an approval writes a typed value over the same field.
   */
  sources: { fullName: IdentitySource; dateOfBirth: IdentitySource };
  /** False once a newer check replaced this one. */
  current: boolean;
  supersededAt: string | null;
  /** When the photographs were deleted under the retention period, if they were. */
  imagesPurgedAt: string | null;
  imagesHeld: IdentityRecordFacts['imagesHeld'];
  submittedAt: string | null;
  recordedAt: string;
  /**
   * How many of this player's checks a person has refused so far, current or replaced. The refusal
   * dialog counts the refusal being made as one more, and says from that and the reason whether the
   * player will be able to send new photos.
   */
  refusalsSoFar: number;
  /**
   * SUPPORT'S ONE MORE TRY (OneMoreTry.tsx): whether "Allow one more try" would be taken on this check
   * now, and if not the press's own sentence saying why — the case route reads the decision the press
   * is answered by — and who allowed one more try after this refusal, when and why. Absent from an
   * API older than this console, which offers no such press.
   */
  oneMoreTry?: IdentityOneMoreTry;
}

/** A check already decided, as the Decided tab lists it. */
export interface DecidedRow {
  id: string;
  playerId: string;
  playerRef: string;
  playerName: string;
  brandName: string | null;
  outcome: string;
  provider: string;
  decision: Pick<RecordDecision, 'by' | 'personName' | 'at'>;
}

/**
 * WHETHER THIS CASE MAY BE DECIDED: a current check, waiting for a person.
 *
 * The verdict route refuses anything else ("already settled", "still with the identity
 * service"), so a decided check opened from the Decided tab draws no verdict button rather than
 * one that could only be refused — and neither does a waiting check that a newer one replaced.
 */
export function verdictIsOpen(c: Pick<CaseDetail, 'outcome' | 'current'>): boolean {
  return c.outcome === 'REVIEW' && c.current;
}

/* WHETHER CIRA HOLDS NO CHECK THIS PLATFORM CAN POINT TO — `noCiraCheckOnRecord`, from
   @dayon/contracts, the one rule the case and the queue route share: such a check cannot be
   approved (sql/992), so the case says so before anybody types and closes Verify, and the queue
   tags it "Cannot be approved" where the reviewer picks the work. */

/**
 * WHETHER CIRA'S ANSWER ON THE NAME SAYS ANYTHING ABOUT THE PLAYER.
 *
 * Always, for a name the player gave at sign-up: CIRA was sent it whole, however many words it
 * has. For an account opened before sign-up asked, only where its display name has a surname in it
 * — a one-word handle sent CIRA half a name, and its "they differ" is this platform's missing data.
 */
function nameIsComparable(c: Pick<CaseDetail, 'declared'>): boolean {
  return (
    c.declared.nameGivenAtSignUp ||
    nameWasCorrected(c.declared) ||
    splitPersonName(c.declared.fullName).comparable
  );
}

/**
 * WHETHER THE NAME CIRA WAS SENT IS ONE THE PLAYER CORRECTED AFTER A REFUSAL — then it is never called
 * the name given at sign-up. A correction of the date of birth alone leaves the sign-up name.
 */
function nameWasCorrected(declared: CaseDetail['declared']): boolean {
  return declared.correction?.nameChanged === true;
}

/** What the case route writes after the name of a player whose ID shows only one name. */
const ONE_NAME_ONLY = ' (one name only)';

/**
 * WHETHER THE PLAYER GAVE ONE NAME AT SIGN-UP — an ID that shows only one name.
 *
 * The case route writes such a name the way every console screen shows it, "Sukarno (one name
 * only)" (`formatDeclaredNameForStaff`, @dayon/contracts), and that mark is the only sign of it the
 * case carries. No typed name can end with it: a name part holds letters, spaces and . ' - only.
 * The settling job reads the same rows the same way (`single_name`, or no first name) before it
 * words CIRA's answer on them (`ONE_NAME`).
 */
function gaveOneNameAtSignUp(declared: CaseDetail['declared']): boolean {
  return (
    declared.nameGivenAtSignUp &&
    !nameWasCorrected(declared) &&
    (declared.fullName?.endsWith(ONE_NAME_ONLY) ?? false)
  );
}

/** The same, for a name the player corrected after a refusal to the one name their ID shows. */
function correctedToOneName(declared: CaseDetail['declared']): boolean {
  return nameWasCorrected(declared) && (declared.fullName?.endsWith(ONE_NAME_ONLY) ?? false);
}

/** The case as one record, for the panel the player's KYC tab also draws. */
function factsOfCase(c: CaseDetail): IdentityRecordFacts {
  return {
    id: c.id,
    outcome: c.outcome,
    provider: c.provider,
    current: c.current,
    submittedAt: c.submittedAt,
    recordedAt: c.recordedAt,
    supersededAt: c.supersededAt,
    fullName: c.document.fullName,
    dateOfBirth: c.document.dateOfBirth,
    sources: c.sources,
    liveness: c.biometric.liveness,
    confidence: c.document.confidence,
    decision: c.decision,
    imagesHeld: c.imagesHeld,
    imagesPurgedAt: c.imagesPurgedAt,
    screening: c.providerSaid.screening,
    advice: c.providerSaid.advice,
    laterAnswer: c.providerSaid.laterAnswer,
    noCiraCheckOnRecord: noCiraCheckOnRecord(c),
  };
}

const manila = new Intl.DateTimeFormat('en-PH', {
  timeZone: 'Asia/Manila',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});
const when = (iso: string | null) => (iso === null ? '—' : `${manila.format(new Date(iso))}`);

/* The photographs' labels moved with the photographs, to IdentityPhotos.tsx: the player's KYC
   tab shows them through the same panel. */

/**
 * How a confidence reads to a person.
 *
 * The number is the point and is always shown — but a bare 0.62 beside a 0.96
 * makes a reviewer compute the comparison themselves, every row, on every case.
 *
 * `TagTone` here is 'accent' | 'neutral' | 'outline' | 'quiet': this design system
 * deliberately has no red or green on a Tag, so emphasis is all that is available
 * and the WORDS have to carry the meaning. That is the accessible reading anyway —
 * a reviewer with a colour deficiency gets the same information as anybody else.
 */
function confidenceTone(value: number): TagTone {
  if (value >= 0.9) return 'quiet';
  if (value >= 0.8) return 'neutral';
  return 'accent';
}

/**
 * The figure the provider gave one field, where it gave one per field.
 *
 * The full name reads its OWN figure first. CIRA returns a `full_name` beside the parts, and the
 * record's full name is that one when it came (`identityFrom` in the settling job); the first
 * name's figure is about one word of it, kept only for a read that filled the parts and not the
 * whole. This read `first_name` alone, so a full name read at 0.91 beside a first name at 0.55
 * was shown as 0.55.
 */
function fieldConfidence(confidence: Record<string, number>, key: string): number | undefined {
  if (key === 'full_name') return confidence['full_name'] ?? confidence['first_name'];
  return confidence[key];
}

/**
 * The stored CODE turned back into the words the player was shown.
 *
 * `EMPLOYED_PRIVATE` and `SALARY` are this platform's vocabulary, not anybody's
 * language, and an operator reading a case should see what the player read when they
 * chose it. The same rule that keeps constraint names and table names off these screens.
 *
 * From `@dayon/contracts`, which is where both the player's form and this list get them,
 * so a new option cannot appear on one and not the other.
 */
const WORK_WORDS = new Map<string, string>(NATURE_OF_WORK.map((o) => [o.key, o.label]));
const INCOME_WORDS = new Map<string, string>(SOURCE_OF_INCOME.map((o) => [o.key, o.label]));

/* And the document type, which comes from the PROVIDER's vocabulary rather than ours:
   `documentWords` (document-words.ts), the one map the Payout accounts tab reads too. */

/**
 * THE FLOOR THE SETTLING JOB APPLIES, repeated here so the screen can explain a score
 * rather than only colour it. `settle-identity-checks` owns the decision; this says the
 * same number out loud.
 */
const FACE_MATCH_FLOOR = 0.8;

/**
 * What a reviewer should look at first, ranked.
 *
 * ── WHY THIS IS COMPUTED AND NOT A SENTENCE FROM THE SERVER ───────────────
 *
 * The server writes ONE note, which is right — it is the record of why the check
 * stopped, and it goes on the row. But a reviewer deciding a case is answering several
 * questions at once, and a paragraph makes them read for the answers. These are the
 * questions, each with the field that answers it, so the ones that should stop a
 * reviewer are visible before they have read anything.
 *
 * NOTHING HERE INVENTS A FINDING. Every entry is conditioned on a value the provider
 * actually reported — the lesson from a note that read "a sanctions or PEP screening
 * hit" on a check whose screening had passed. A null is "not checked", which is its own
 * row rather than an absence, because an unanswered question looks exactly like a
 * passed one if nobody draws it.
 */
type AlertWeight = 'stop' | 'look' | 'note';

interface ReviewAlert {
  weight: AlertWeight;
  icon: string;
  what: string;
  /** What it means for the decision, in the reviewer's terms. */
  means: string;
}

function alertsFor(c: CaseDetail): ReviewAlert[] {
  const out: ReviewAlert[] = [];
  const { document: doc, biometric: bio, declared, providerSaid } = c;
  /*
   * A CIRA CHECK CIRA HOLDS NOTHING FOR changes more than the first finding. Approving it is not
   * open, so no sentence below may advise on approving it. And in one of its three causes — the
   * platform could not record the reference CIRA gave — CIRA DID receive the documents, and may
   * have read and screened them; the platform simply holds none of it. So whatever such a check
   * lacks is said as not ON RECORD, which is true whichever the cause, and never as something
   * CIRA did not do or a photograph on the card that could not be found.
   */
  const noCiraCheck = noCiraCheckOnRecord(c);

  /*
   * ── A CIRA CHECK CIRA HOLDS NOTHING FOR, FIRST — `noCiraCheckOnRecord` ──────
   *
   * It decides what the reviewer can do at all, so it comes before anything they might read the
   * photographs for. The job's note below says which of the three causes it was.
   *
   * BOTH CHOICES, and what each means for the player. Left open, nothing moves it: nobody can
   * approve it, and the player's screen goes on saying a person is checking their documents by
   * hand (`eligibility`, a check waiting for a person) — this said only what refusing costs, which
   * read as advice to leave such checks waiting for good. Refused, the player's screen follows the
   * reason and their tries (`eligibility`): for a reason they can put right, while they have tries
   * left, it says what to fix and takes new photographs; otherwise it tells them to raise a support
   * ticket. The refusal dialog says which, once a reason is chosen.
   */
  if (noCiraCheck) {
    out.push({
      weight: 'stop',
      icon: 'warning',
      what: 'No CIRA check is on record for these documents',
      means:
        'An approval has to rest on CIRA’s own check, so this one cannot be approved. Left open, it ' +
        'stays in this queue with nobody able to approve it, and the player keeps being told that ' +
        'someone is checking their documents by hand. It can still be refused — whether the ' +
        'player may then send new photos depends on the reason you choose and the tries they ' +
        'have left.',
    });
  }

  /*
   * ── CIRA'S OWN ADVICE, FIRST — 15 Oct 2026 ────────────────────────────────
   *
   * CIRA sends `advisory_status: "REJECTED"` with a sanctions match, and the platform read it
   * nowhere, so the person deciding never saw it. It goes at the top with the match beneath it:
   * the two things a reviewer must have read before anything else on the case. Shown, never
   * obeyed — nothing automated refuses a player — and any word but REJECTED is quoted as CIRA
   * wrote it, because REJECTED is the only one CIRA has documented.
   */
  if (providerSaid.advice !== null) {
    out.push(
      advisesRejecting(providerSaid.advice)
        ? {
            weight: 'stop',
            icon: 'warning',
            what: 'CIRA advised rejecting this player',
            means:
              'Shown, not followed: nothing automated refuses a player, so the decision is yours. ' +
              'Read what CIRA matched and the photographs before deciding.',
          }
        : {
            weight: 'look',
            icon: 'info',
            what: `CIRA’s advice: “${providerSaid.advice}”`,
            means: 'CIRA’s own word, carried through untranslated.',
          },
    );
  }

  /*
   * ── THE SCREENING, WHICH THIS PANEL COULD NOT SEE ─────────────────────────
   *
   * `alertsFor` had no branch for sanctions or PEP, because the API carried no
   * field for them — the provider returns the screening with every result and the
   * platform dropped it. So a case whose ONLY finding was a sanctions match arrived
   * with an empty alert list, and the drawer asserted "Nothing here is flagged".
   *
   * THREE OUTCOMES, THREE SENTENCES, and the third is the one that matters:
   * "nobody screened this player" is not "this player screened clean". The step
   * dies on its own — observed as "Sanctions & PEP screening DEAD — OpenSanctions
   * rejected the request (HTTP 401)" on the provider's own dashboard — and a
   * reviewer who reads an absent verdict as a clean one is the failure this whole
   * panel exists to prevent.
   */
  if (providerSaid.screening.sanctionsHit === true) {
    out.push({
      weight: 'stop',
      icon: 'warning',
      what: 'The screening matched a sanctions list',
      means:
        'The service matched this subject, not this document. Names collide — check the ' +
        'date of birth and the nationality against the match before deciding, and escalate ' +
        'rather than refuse if they line up.',
    });
  }
  if (providerSaid.screening.pepHit === true) {
    out.push({
      weight: 'stop',
      icon: 'warning',
      what: 'The screening matched a politically exposed person',
      means:
        'Not a refusal on its own — a PEP may hold an account. It is a reason for enhanced ' +
        'due diligence, and the decision belongs above this screen.',
    });
  }
  /*
   * ── A LIST NOBODY SCREENED, SAID PLAINLY — 15 Oct 2026 ─────────────────────
   *
   * CIRA switched sanctions and PEP screening off for this account at 8:22 PM that day, so
   * every new check arrives with both lists unscreened — the commonest reason a check is here
   * now, and not one a reviewer can fix by looking at photographs. Each list is its own
   * question (ruling R5): a null on either one is "not screened", whatever CIRA's overall
   * verdict says. This said "No sanctions or PEP screening was performed" and blamed the
   * service's data source; the cause today is a switch at CIRA, and the sentence names who did
   * not screen it without guessing why.
   */
  const { sanctionsHit, pepHit } = providerSaid.screening;
  /* On a check CIRA holds nothing for, "CIRA did not screen" may be untrue (it may have screened a
     check whose reference the platform could not record), and approving is not open: the screening
     is said as not on record, with no advice about approving. */
  if (sanctionsHit === null && pepHit === null) {
    out.push(
      noCiraCheck
        ? {
            weight: 'look',
            icon: 'info',
            what: 'No sanctions or PEP screening is on record for this check',
            means:
              'No CIRA result is on record for these documents, so nothing here says whether the ' +
              'player is on either list. This is not a clean screening.',
          }
        : {
            weight: 'look',
            icon: 'info',
            what:
              c.provider === 'CIRA'
                ? 'CIRA did not screen this check for sanctions or PEP'
                : 'No identity service screened this check for sanctions or PEP',
            means:
              'Approving it does not screen the player. This is not a clean screening: none happened.',
          },
    );
  } else if (sanctionsHit === null || pepHit === null) {
    out.push(
      noCiraCheck
        ? {
            weight: 'look',
            icon: 'info',
            what:
              sanctionsHit === null
                ? 'No screening against the sanctions lists is on record for this check'
                : 'No screening for politically exposed persons is on record for this check',
            means:
              'No CIRA result on that list is on record for these documents. This is not a clean ' +
              'screening.',
          }
        : {
            weight: 'look',
            icon: 'info',
            what:
              sanctionsHit === null
                ? 'CIRA did not screen this check against the sanctions lists'
                : 'CIRA did not screen this check for politically exposed persons',
            means: 'Approving it does not screen the player against that list.',
          },
    );
  }

  if (doc.fullName === null) {
    out.push({
      weight: 'stop',
      icon: 'warning',
      /* Not "nothing was read" on a check CIRA holds nothing for: CIRA may have read it. */
      what: noCiraCheck
        ? 'No reading of the document is on record'
        : 'Nothing was read from the document',
      /* On a check CIRA holds nothing for, approving is not open at all (the finding above), so
         the advice is about the photographs alone. */
      means: noCiraCheck
        ? 'There is nothing to compare with the account. Open the photographs before deciding.'
        : 'Approving means typing what the ID shows yourself — the name exactly as it is printed. ' +
          'Open the photographs first.',
    });
  }

  if (providerSaid.nameMatchesDeclared === false) {
    /*
     * ── TWO DIFFERENT THINGS A "NO MATCH" CAN MEAN ────────────────────────────
     *
     * The account's name is a DISPLAY NAME, which is a handle. A one-word handle
     * has no surname in it, so the service was handed half a name and will report
     * a mismatch for every such player. Rendering that as a finding about the
     * person reports this platform's missing data as the player's problem.
     *
     * ── AND THE SENTENCE THAT USED TO SIT HERE WAS FALSE ──────────────────────
     *
     * It read: "A middle name the account omits is not a mismatch and the service
     * allows for that, so this is a real difference." That was an assumption about
     * CIRA's behaviour with nothing behind it, and a real verification disproved
     * it — the platform had never sent a middle name at all, because the job
     * creating the verification populated neither `middleName` nor `suffix`. The
     * sentence actively argued a reviewer INTO a false conclusion: it said the
     * easy explanation had been ruled out when nobody had ruled it out.
     *
     * What replaces it says only what is known: the service compared its reading
     * against the name this platform holds, and the reviewer has the photographs.
     *
     * ── AND THE NAME THE PLAYER GAVE AT SIGN-UP, WHEN THERE IS ONE ────────────
     *
     * Sign-up asks for the name exactly as the ID prints it, and CIRA is sent that name
     * whole, so its "they differ" is about the player whatever the name's length — the
     * sentence says whose name it is. The two sentences after it are for an account
     * opened before sign-up asked, and say what they said before; the second uses the
     * settling job's own words for the same case (`NO_FULL_NAME`).
     *
     * ── EXCEPT FOR A PLAYER WHOSE ID SHOWS ONE NAME (22 Oct 2026) ─────────────
     *
     * CIRA needs a first and a last name, so the start job sends the one name as both, and
     * CIRA's "they differ" may be that doubling ("SUKARNO SUKARNO" against the ID's "SUKARNO")
     * rather than the player. This said "is not the name the player gave at sign-up" anyway,
     * directly above the settling job's note saying what was sent. So the finding says what the
     * note says, in the settling job's own words (`ONE_NAME_SENT_TWICE`), and blames nobody; a
     * person decides it either way.
     */
    if (nameWasCorrected(declared)) {
      /* A NAME THE PLAYER CORRECTED AFTER A REFUSAL is what CIRA was sent, and it is never called the
         name given at sign-up: the settling job's own words for it (`NAME_DIFFERS_FROM_CORRECTION`,
         `CORRECTED_ONE_NAME_SENT_TWICE`). */
      out.push(
        correctedToOneName(declared)
          ? {
              weight: 'stop',
              icon: 'warning',
              what:
                'CIRA was sent the one name the player gave when they corrected their details as ' +
                'both a first and a last name, and says the document shows a different name',
              means:
                'CIRA needs a first and a last name, so the one name was sent as both — the ' +
                'difference CIRA reports may be that doubling rather than the player. Open the ' +
                'photographs and read the printed name yourself before deciding.',
            }
          : {
              weight: 'stop',
              icon: 'warning',
              what: 'The name on the document is not the corrected name the player gave after a refusal',
              means:
                'CIRA compared its reading of the ID with the name the player corrected after a ' +
                'refusal. Open the photographs and read the printed name yourself before deciding.',
            },
      );
    } else if (declared.nameGivenAtSignUp) {
      out.push(
        gaveOneNameAtSignUp(declared)
          ? {
              weight: 'stop',
              icon: 'warning',
              what:
                'CIRA was sent the one name the player gave at sign-up as both a first and a last ' +
                'name, and says the document shows a different name',
              means:
                'CIRA needs a first and a last name, so the one name was sent as both — the ' +
                'difference CIRA reports may be that doubling rather than the player. Open the ' +
                'photographs and read the printed name yourself before deciding.',
            }
          : {
              weight: 'stop',
              icon: 'warning',
              what: 'The name on the document is not the name the player gave at sign-up',
              means:
                'CIRA compared its reading of the ID with the name the player gave at sign-up. ' +
                'Open the photographs and read the printed name yourself before deciding.',
            },
      );
    } else if (nameIsComparable(c)) {
      out.push({
        weight: 'stop',
        icon: 'warning',
        what: 'The name on the document is not the name on the account',
        means:
          'The service compared its reading against the name this account carries. ' +
          'Open the photographs and read the printed name yourself before deciding.',
      });
    } else {
      out.push({
        weight: 'look',
        icon: 'info',
        what: 'The account has no full name for CIRA to compare',
        means:
          `No name was asked at sign-up for this account, and its display name “${declared.fullName}” ` +
          'has no surname in it, so CIRA had no full name to compare and its mismatch says nothing ' +
          'about this player. The name on the document is the one that counts — read it from the ' +
          'photographs.',
      });
    }
  }

  if (providerSaid.documentExpired === true) {
    out.push({
      weight: 'stop',
      icon: 'calendar-slash',
      what: 'The document was expired when it was presented',
      means: 'An expired ID does not evidence identity on the day it was given.',
    });
  }

  const dobDiffers =
    doc.dateOfBirth !== null && declared.dateOfBirth !== null && doc.dateOfBirth !== declared.dateOfBirth;
  /* THE DATE THIS CHECK WAS SENT. While the account still holds it, that is "the one on the account";
     once a correction after this check changed it, the account no longer does, so the finding names
     the date by where it came from — sign-up, or a correction before this check — and says what the
     player corrected it to since. */
  const dobSince = declared.laterCorrection?.dateOfBirth ?? null;
  if (dobDiffers && dobSince === null) {
    out.push({
      weight: 'stop',
      icon: 'warning',
      what: 'The date of birth on the document is not the one on the account',
      means: `The account says ${declared.dateOfBirth}; the document says ${doc.dateOfBirth}.`,
    });
  } else if (dobDiffers) {
    out.push({
      weight: 'stop',
      icon: 'warning',
      what:
        declared.correction?.dateOfBirthChanged === true
          ? 'The date of birth on the document is not the corrected one the player gave after a refusal'
          : 'The date of birth on the document is not the one the player gave at sign-up',
      means:
        `This check was sent ${declared.dateOfBirth}; the document says ${doc.dateOfBirth}. ` +
        `After this check the player corrected it to ${dobSince}.`,
    });
  }

  if (bio.liveness !== null && bio.liveness.toLowerCase() !== 'passed') {
    out.push({
      weight: 'stop',
      icon: 'warning',
      what: `The liveness check did not pass (${bio.liveness})`,
      means: 'Nothing establishes that a live person was in front of the camera.',
    });
  }

  if (bio.faceMatchScore === null) {
    out.push(
      /* On a check CIRA holds nothing for, the cause is that, not the card's photograph. */
      noCiraCheck
        ? {
            weight: 'look',
            icon: 'warning-circle',
            what: 'No face match is on record',
            means:
              'No CIRA result is on record for these documents, so nothing here compares the ' +
              'selfie with the photograph on the ID.',
          }
        : {
            weight: 'look',
            icon: 'warning-circle',
            what: 'No face match was produced',
            means:
              'Nothing connects the person to the document. Usually the photograph on the card ' +
              'could not be found — look at the front of the ID.',
          },
    );
  } else if (bio.faceMatchScore < FACE_MATCH_FLOOR) {
    out.push({
      weight: 'stop',
      icon: 'warning',
      what: `The face match scored ${faceMatchWords(bio.faceMatchScore)}`,
      means: `Below the ${FACE_MATCH_FLOOR.toFixed(2)} floor this platform requires.`,
    });
  }

  const docConfidence = c.document.confidence[DOCUMENT_CONFIDENCE_KEY];
  if (docConfidence !== undefined && docConfidence < 0.6) {
    out.push({
      weight: 'look',
      icon: 'warning-circle',
      what: `The service rated its own read ${docConfidence.toFixed(2)}`,
      means: 'A poor read. Check every field below against the photograph rather than trusting it.',
    });
  }

  if (providerSaid.failedStep !== null) {
    out.push({
      weight: 'note',
      icon: 'info',
      what: `The service stopped at "${providerSaid.failedStep}"`,
      means: 'Its own word for the stage, carried through untranslated.',
    });
  }

  /* NOT CHECKED IS NOT PASSED. A case a person is reading has no service verdicts at
     all, and a panel that simply showed nothing would read as "all clear". Said of the
     name the player gave at sign-up, when there is one. */
  if (providerSaid.nameMatchesDeclared === null && doc.fullName !== null) {
    out.push(
      nameWasCorrected(declared)
        ? {
            weight: 'note',
            icon: 'info',
            what: 'The name was not checked against the corrected name the player gave after a refusal',
            means: 'No identity service compared the two — compare them yourself below.',
          }
        : declared.nameGivenAtSignUp
        ? {
            weight: 'note',
            icon: 'info',
            what: 'The name was not checked against the name the player gave at sign-up',
            means: 'No identity service compared the two — compare them yourself below.',
          }
        : {
            weight: 'note',
            icon: 'info',
            what: 'The name was not cross-checked against the account',
            means: 'No service verdict on this record — compare the two yourself below.',
          },
    );
  }

  const order: Record<AlertWeight, number> = { stop: 0, look: 1, note: 2 };
  return out.toSorted((a, b) => order[a.weight] - order[b.weight]);
}

/**
 * A face-match score, printed at the precision the provider actually reports.
 *
 * ── WHY THIS IS A FUNCTION AND NOT A `toFixed` AT EACH CALL SITE ──────────
 *
 * It was three `toFixed` calls. Two were corrected to four places and the third — the
 * queue's own column — was missed, so the list went on printing "1.00" beside a drawer
 * that said "0.9996" for the same check. The identical shape of bug this screen has been
 * correcting all day: one value, several places, one of them left behind.
 *
 * FOUR PLACES because CIRA reports the score as a percentage to two decimals, which is
 * four as a fraction. Printing 0.9996 as "1.00" tells a reviewer the two faces are
 * identical with no residual doubt, which the service did not say — the same
 * manufactured certainty sql/998 widened the column to stop storing.
 */
function faceMatchWords(score: number): string {
  return score.toFixed(4);
}

/**
 * THE QUEUE'S "WHY A PERSON" CELL: the job's own sentence, and what the case will ask of the
 * reviewer, visible while they pick the work.
 *
 * THE JOB'S OWN SENTENCE, not the provider's status. "face match 0.71 below 0.8" is something a
 * reviewer can act on; "manual_review_required" is a status for a system.
 *
 * A CHECK CIRA HOLDS NOTHING FOR CANNOT BE APPROVED (sql/992), and it was tagged "Nothing read —
 * you type the details": a promise of approval by typing, on the one check nobody can approve,
 * made before the reviewer had even opened the case that says so. It is tagged for what it is,
 * in the case's own words; "you type the details" stays for a check with nothing read that CAN
 * be approved. The reason sits under the tag rather than in it: a tag does not wrap, and the
 * whole sentence would run out of a phone's card.
 *
 * Exported so a test can render the cell without the queue's reads.
 */
export function QueueReason({
  row,
}: {
  row: Pick<QueueRow, 'reviewNote' | 'hasExtractedIdentity' | 'noCiraCheckOnRecord'>;
}) {
  return (
    <>
      <span>{row.reviewNote ?? 'Sent for review'}</span>
      {row.noCiraCheckOnRecord ? (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <Tag tone="accent">Cannot be approved</Tag>
          <div className="text-subtle" style={{ fontSize: '11.5px', marginTop: 'var(--space-1)' }}>
            No CIRA check is on record for these documents.
          </div>
        </div>
      ) : (
        !row.hasExtractedIdentity && (
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Tag tone="accent">Nothing read — you type the details</Tag>
          </div>
        )
      )}
    </>
  );
}

/** How often the queue looks again. See the effect that uses it for why thirty. */
const QUEUE_REFRESH_MS = 30_000;

/** The screen's two lists: the work, and the record of work done. */
const IDENTITY_TABS = [
  { key: 'waiting', label: 'Waiting for a person' },
  { key: 'decided', label: 'Decided' },
] as const;
type IdentityTab = (typeof IDENTITY_TABS)[number]['key'];

/** Decided checks per page. Every one is reachable: the list pages, newest decision first. */
const DECIDED_PER_PAGE = 50;

/**
 * THE DECIDED TAB'S LIST, AND WHERE THE READ OF IT STANDS.
 *
 * "Still being read", "the read failed" and "it came back empty" are three different facts, and
 * only the last is "nothing decided yet". A failed read used to leave an empty list behind it, so
 * the tab drew "Nothing decided yet — No identity check has been verified or refused yet in the
 * brands you can see." beneath the error dialog: a real zero, said of a list nobody had read.
 */
export interface DecidedList {
  read: 'loading' | 'loaded' | 'failed';
  rows: DecidedRow[];
  meta: PageMeta | null;
}

/** The Decided tab before its first read. */
const DECIDED_UNREAD: DecidedList = { read: 'loading', rows: [], meta: null };

/**
 * One read of the Decided tab, settled into what the tab shows: the page the read returned or,
 * when it failed, a list that says it did not load. Never an empty list, which is the real zero.
 * The failure itself goes to `onFailure`, which opens the error dialog.
 *
 * The request stays in the screen's own `loadDecided`, behind its `canReview` guard, where
 * `capability-gate.test.ts` can see it; this only settles it.
 */
export async function settleDecided(
  read: Promise<{ items: DecidedRow[]; meta: PageMeta }>,
  onFailure: (error: unknown) => void,
): Promise<DecidedList> {
  try {
    const data = await read;
    return { read: 'loaded', rows: data.items, meta: data.meta };
  } catch (error) {
    onFailure(error);
    return { read: 'failed', rows: [], meta: null };
  }
}

/** `accent` is this system's attention colour — see `confidenceTone`, which uses it for the worst case. */
function alertTone(weight: AlertWeight): TagTone {
  if (weight === 'stop') return 'accent';
  if (weight === 'look') return 'neutral';
  return 'quiet';
}

/**
 * What the account says, field by field — the flag that says which name it holds, the first entry
 * beside a correction, and a correction made after the check, are not fields.
 */
type DeclaredField = keyof Omit<
  CaseDetail['declared'],
  'nameGivenAtSignUp' | 'correction' | 'laterCorrection'
>;
/** What the document says, field by field. */
type DocumentField = keyof Omit<CaseDetail['document'], 'confidence'>;

/**
 * The rows of the comparison, and which of them carry a verdict.
 *
 * A verdict is only offered where the two values are claims about the SAME fact. The
 * name has one because the service computes it — and computing it here instead would
 * refuse every player whose document carries a middle name their account omits. The
 * date of birth has one because both sides are a calendar day in the same format.
 *
 * Everything else is shown without a verdict, including the fields the document has and
 * the account does not: a reviewer needs to see a place of birth and an address, and
 * there is nothing on the account to disagree with them.
 */
const COMPARISONS: readonly {
  label: string;
  declared: DeclaredField | null;
  document: DocumentField;
  /** The tag beside the row's label — who compared the two, and what they found — or none. */
  verdict?: (c: CaseDetail) => { tone: TagTone; words: string } | null;
}[] = [
  {
    label: 'Full name',
    declared: 'fullName',
    document: 'fullName',
    verdict: nameVerdict,
  },
  {
    label: 'Date of birth',
    declared: 'dateOfBirth',
    document: 'dateOfBirth',
    /* THIS CONSOLE's comparison: two calendar days in one format, compared exactly. */
    verdict: (c) => {
      if (c.document.dateOfBirth === null || c.declared.dateOfBirth === null) return null;
      return c.document.dateOfBirth === c.declared.dateOfBirth
        ? { tone: 'quiet', words: 'They agree' }
        : { tone: 'accent', words: 'They differ' };
    },
  },
  { label: 'Place of birth', declared: null, document: 'placeOfBirth' },
  { label: 'Address', declared: null, document: 'currentAddress' },
  { label: 'Document', declared: null, document: 'documentType' },
  { label: 'Document number', declared: null, document: 'documentNumber' },
  { label: 'Expires', declared: null, document: 'documentExpiry' },
];

/**
 * THE NAME ROW'S TAG: CIRA's answer, said as CIRA's.
 *
 * The SERVICE's comparison, not ours — and the date row directly below carries "They agree" for
 * THIS CONSOLE's own exact comparison of two days, so the name's verdict names who gave it: "CIRA:
 * they agree", "CIRA: they differ". Drawn alike, a reviewer could not tell who had compared.
 *
 * No verdict where CIRA's answer says nothing about the player: an account with no full name to
 * compare — a one-word handle has no surname in it, so a service handed half a name reports a
 * mismatch for everybody, and "they differ" beside two strings a reviewer can read as the same name
 * is the screen vouching for a comparison that was never possible. A name the player gave at
 * sign-up was sent whole, so CIRA's answer on it always stands (`nameIsComparable`).
 *
 * "CIRA did not compare" where CIRA holds the check and gave no answer on the name: not checked is
 * not passed, and a row with no tag reads as nothing to see. Never on a check CIRA was not asked to
 * read, one it has not answered yet, or one with no CIRA reference on record — CIRA may have
 * compared that one, and the platform holds none of it (`noCiraCheckOnRecord`).
 *
 * "CIRA’s answer not on record" instead on a check settled before the platform kept that answer
 * (`NAME_ANSWER_KEPT_FROM`): its silence says nothing about what CIRA did — CIRA may well have
 * compared — so "did not compare" would be a claim the code cannot make.
 */
function nameVerdict(c: CaseDetail): { tone: TagTone; words: string } | null {
  const answer = c.providerSaid.nameMatchesDeclared;
  if (answer !== null) {
    if (!nameIsComparable(c)) return null;
    return answer
      ? { tone: 'quiet', words: 'CIRA: they agree' }
      : { tone: 'accent', words: 'CIRA: they differ' };
  }
  const ciraHoldsIt = c.provider === 'CIRA' && c.outcome !== 'PENDING' && !noCiraCheckOnRecord(c);
  if (!ciraHoldsIt) return null;
  if (c.recordedAt < NAME_ANSWER_KEPT_FROM) {
    return { tone: 'neutral', words: 'CIRA’s answer not on record' };
  }
  return { tone: 'neutral', words: 'CIRA did not compare' };
}

/**
 * WHEN THE PLATFORM BEGAN KEEPING CIRA'S ANSWER ON THE NAME: sql/999, on 7 October 2026, from
 * Manila midnight — 16:00 on 6 October in UTC. `recordedAt` is when the check's reading was written,
 * as `toISOString()` prints it, so the two compare as text.
 */
const NAME_ANSWER_KEPT_FROM = '2026-10-06T16:00:00.000Z';

/**
 * The label over the account's side of a comparison row. For the name, WHICH name: the one the
 * player gave at sign-up, or — for an account opened before sign-up asked — its display name,
 * labelled as one, so nobody reads a handle as a legal name. The label names the first entry; a
 * correction after a refusal sits under it (`AccountSide`). For the date of birth, the same: what was
 * given at sign-up, once a correction — before this check or after it — changed it.
 */
function accountSideLabel(c: CaseDetail, field: DeclaredField): string {
  if (field === 'dateOfBirth') {
    const corrected =
      c.declared.correction?.dateOfBirthChanged === true ||
      (laterCorrectionOf(c)?.dateOfBirth ?? null) !== null;
    return corrected ? 'Given at sign-up' : 'On the account';
  }
  if (field !== 'fullName') return 'On the account';
  return c.declared.nameGivenAtSignUp
    ? 'Name given at sign-up'
    : 'Display name (no name was asked at sign-up)';
}

/** A correction made after this check, or null — also for an API older than this console. */
function laterCorrectionOf(c: CaseDetail): NonNullable<CaseDetail['declared']['laterCorrection']> | null {
  return c.declared.laterCorrection ?? null;
}

/**
 * THE ACCOUNT'S SIDE OF A ROW: the first entry, and under it — where a correction after a refusal
 * changed it — "Corrected on <day> after a refusal" with the value the player gave then. "Yes, keep the
 * first": both are drawn.
 *
 * WHAT THE CHECK WAS SENT IS THE VALUE THE ROW'S VERDICT COMPARES: the correction made before this
 * check, else the first entry. A correction made AFTER it is drawn last, as a later fact, and says so
 * — "Made after this check, so this check is about the name above" — or the row would set CIRA's tag
 * beside a value CIRA was never sent.
 */
function AccountSide({ c, field, isName }: { c: CaseDetail; field: DeclaredField; isName: boolean }) {
  const { correction } = c.declared;
  const sent = c.declared[field];
  const changed =
    correction !== null &&
    ((field === 'fullName' && correction.nameChanged) ||
      (field === 'dateOfBirth' && correction.dateOfBirthChanged));
  const laterCorrection = laterCorrectionOf(c);
  const later =
    laterCorrection === null
      ? null
      : field === 'fullName'
        ? laterCorrection.fullName
        : field === 'dateOfBirth'
          ? laterCorrection.dateOfBirth
          : null;
  /* Which of the values are names the player GAVE — at sign-up, or in a correction — and so are
     set as the ID prints one. An account opened before sign-up asked shows its display name. */
  const signUpIsName = isName && c.declared.nameGivenAtSignUp;
  if (!changed && later === null)
    return <ComparedValue value={sent} isName={isName} givenName={signUpIsName || (isName && nameWasCorrected(c.declared))} />;
  const first = !changed
    ? sent
    : field === 'fullName'
      ? c.declared.nameGivenAtSignUp
        ? correction.signUpName
        : c.playerName
      : correction.signUpDateOfBirth;
  return (
    <div>
      <ComparedValue value={first} isName={isName} givenName={signUpIsName} />
      {changed && (
        <CorrectedAfterRefusal day={calendarDay(correction.correctedOn)}>
          <ComparedValue value={sent} isName={isName} givenName={isName} />
        </CorrectedAfterRefusal>
      )}
      {later !== null && laterCorrection !== null && (
        <CorrectedAfterRefusal
          day={calendarDay(laterCorrection.correctedOn)}
          afterThisCheck={field === 'fullName' ? 'name' : 'date'}
        >
          <ComparedValue value={later} isName={isName} givenName={isName} />
        </CorrectedAfterRefusal>
      )}
    </div>
  );
}

/**
 * The label over the document's side: where the value came from — the service's reading, or a
 * reviewer's typing over it — and for the name, that it is the ID's.
 */
function documentSideLabel(source: IdentitySource, field: DocumentField, compared: boolean): string {
  if (field === 'fullName') {
    if (source === 'REVIEWER') return 'Name typed from the ID';
    if (source === 'NOT_RECORDED') return 'Name on the ID, or typed from it';
    return 'Name on the ID';
  }
  if (source === 'REVIEWER') return 'Typed from the document';
  if (source === 'NOT_RECORDED') return 'On the document, or typed from it';
  return compared ? 'On the document' : 'From the document';
}

/** One side of a comparison row: its label and its value, on the row's shared two lines. */
const SIDE_OF_A_ROW = { display: 'grid', gridRow: 'span 2', gridTemplateRows: 'subgrid' } as const;

/**
 * A NAME THE PLAYER GAVE, SET THE WAY AN ID PRINTS ONE: in capitals, so the reviewer reads it
 * letter against the name on the ID beside it and a difference of case is never mistaken for a
 * difference of name (the owner's ruling, 26 October 2026). Only the name: the console's own words
 * after a one-name entry, " (one name only)", are not the ID's and stay as they are. A display
 * name is a handle, not a name, and is never passed through this — in capitals it would pass for
 * one.
 */
function asOnId(name: string): string {
  const [own, mark] = name.endsWith(ONE_NAME_ONLY) ? [name.slice(0, -ONE_NAME_ONLY.length), ONE_NAME_ONLY] : [name, ''];
  return own.toLocaleUpperCase('en-PH') + mark;
}

/**
 * One side's value. A name sits in its own isolated box (`PersonName`); anything else wraps. A
 * name the player gave (`givenName`) is set as the ID prints one (`asOnId`).
 */
function ComparedValue({ value, isName, givenName = false }: { value: string | null; isName: boolean; givenName?: boolean }) {
  if (isName && value !== null) return <PersonName>{givenName ? asOnId(value) : value}</PersonName>;
  return <div style={{ overflowWrap: 'anywhere' }}>{value ?? '—'}</div>;
}

const ALERT_WORD: Record<AlertWeight, string> = {
  stop: 'Decide on this',
  look: 'Worth a look',
  note: 'For context',
};

/** The queue's own age colouring. A case waiting a week is a person who cannot deposit. */
function waitingTone(days: number): TagTone {
  if (days >= 3) return 'accent';
  if (days >= 1) return 'neutral';
  return 'quiet';
}

/**
 * The queue's columns: whose check, why it needs a person (`QueueReason`), the face match, how many
 * photographs are held and how long it has waited. Exported so a test can draw the queue's real
 * rows without its reads.
 */
export function queueColumns(onOpen: (id: string) => void): Column<QueueRow>[] {
  return [
    {
      key: 'player',
      header: 'Player',
      render: (r) => (
        <>
          <span className="tabular">{r.playerRef}</span>
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {r.playerName}
            {r.brandName ? ` · ${r.brandName}` : ''}
          </div>
        </>
      ),
    },
    {
      key: 'why',
      header: 'Why a person',
      /* The job's sentence, and a tag for what the case will ask: see `QueueReason`. */
      render: (r) => <QueueReason row={r} />,
    },
    {
      key: 'face',
      header: 'Face match',
      render: (r) =>
        r.faceMatchScore === null ? (
          <Tag tone="accent">None</Tag>
        ) : (
          <Tag tone={confidenceTone(r.faceMatchScore)}>{faceMatchWords(r.faceMatchScore)}</Tag>
        ),
    },
    {
      key: 'images',
      header: 'Photographs',
      render: (r) => <span className="tabular">{formatCount(r.imagesHeld.length)}</span>,
    },
    {
      key: 'waiting',
      header: 'Waiting',
      render: (r) => (
        <>
          <Tag tone={waitingTone(r.waitingDays)}>
            {r.waitingDays === 0 ? 'Today' : `${formatCount(r.waitingDays)}d`}
          </Tag>
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {when(r.recordedAt)}
          </div>
        </>
      ),
    },
    {
      key: 'act',
      header: '',
      render: (r) => (
        <Button variant="secondary" onClick={() => onOpen(r.id)}>
          Open
        </Button>
      ),
    },
  ];
}

/* The Decided tab: whose check, how it ended, and who ended it when. Nothing off the
   document — the case view holds that, one check at a time. */
function decidedColumns(onOpen: (id: string) => void): Column<DecidedRow>[] {
  return [
    {
      key: 'player',
      header: 'Player',
      render: (r) => (
        <>
          <span className="tabular">{r.playerRef}</span>
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {r.playerName}
            {r.brandName ? ` · ${r.brandName}` : ''}
          </div>
        </>
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      /* Only VERIFIED and REFUSED reach this list; neither is ever printed as its code. */
      render: (r) => (
        <Tag tone={outcomeTone(r.outcome)}>{DECIDED_WORDS[r.outcome] ?? 'Decided'}</Tag>
      ),
    },
    {
      key: 'decided',
      header: 'Decided by',
      render: (r) => (
        <>
          <span>{decisionWho(r.decision, r.provider)}</span>
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {momentWords(r.decision.at)}
          </div>
        </>
      ),
    },
    {
      key: 'act',
      header: '',
      render: (r) => (
        <Button variant="secondary" onClick={() => onOpen(r.id)}>
          Open
        </Button>
      ),
    },
  ];
}

/**
 * THE DECIDED TAB'S PANEL: the decided checks a page at a time, newest decision first, or why
 * there is no list to show.
 *
 * A READ THAT FAILED SAYS SO and is never "nothing decided yet": that sentence is a real zero,
 * drawn only from a read that came back with nothing in it. Why the read failed is the error
 * dialog's to say; this says only that the list did not load, and how to read it again. While the
 * first read is in flight the table stands empty and claims neither.
 */
export function DecidedChecks({
  list,
  page,
  onOpen,
  onPage,
  brandName = null,
}: {
  list: DecidedList;
  page: number;
  onOpen: (id: string) => void;
  onPage: (page: number) => void;
  /** The brand chosen in the header, which an empty list is empty for; null under "All". */
  brandName?: string | null | undefined;
}) {
  if (list.read === 'failed') {
    return (
      <EmptyState icon="warning-circle" title="The decided checks did not load">
        The message that opened says why. This does not mean nothing has been decided: press Refresh
        to read the list again.
      </EmptyState>
    );
  }
  if (list.read === 'loaded' && list.rows.length === 0 && page === 1) {
    return (
      <EmptyState icon="check-circle" title="Nothing decided yet">
        {brandName === null
          ? 'No identity check has been verified or refused yet in the brands you can see.'
          : `No identity check in ${brandName} has been verified or refused yet.`}
      </EmptyState>
    );
  }
  return (
    <>
      <DataTable
        rows={list.rows}
        columns={decidedColumns(onOpen)}
        rowKey={(r) => r.id}
        caption="Identity checks already decided, the newest decision first, with the player, the outcome, and who decided and when"
      />
      {list.meta && (
        <Pagination
          page={list.meta.page}
          perPage={list.meta.perPage}
          total={list.meta.total}
          onPage={onPage}
        />
      )}
    </>
  );
}

/**
 * THE QUEUE'S OWN SENTENCES, true whatever CIRA's screening is set to.
 *
 * They said checks arrive "usually because a photograph was hard to read". Since 15 Oct 2026,
 * 8:22 PM CIRA screens nobody on this account for sanctions or PEP, so every new check arrives
 * because nobody screened it — and the owner may switch screening back on at any time, which
 * makes photographs the commoner reason again. So no reason is ranked: the screening is named
 * among the others, and "for example" says the list is not the whole of it.
 */
export function identityQueueWords(brandName: string | null = null) {
  /* With a brand chosen in the header the list is that brand's, and the sentences say so rather
     than speak for every brand the reader can see. */
  const inBrand = brandName === null ? '' : ` in ${brandName}`;
  return {
    automatic:
      'Checks the identity service could not verify on its own, including any it did not screen ' +
      'for sanctions or PEP. Nothing automated refuses a player.',
    nothingWaiting:
      `No identity check${inBrand} needs a person right now. Checks the identity service cannot ` +
      'verify on its own arrive here — a photograph it could not read, for example, or a check ' +
      'it did not screen for sanctions or PEP.',
    inFlight: (count: number) =>
      `${formatCount(count)} ${count === 1 ? 'check' : 'checks'}${inBrand} ` +
      `${count === 1 ? 'is' : 'are'} still with the identity service. Any it cannot verify on ` +
      'its own will arrive here. This list looks again every half a minute.',
  };
}

/**
 * WHAT A ROLE WITHOUT `kyc.review` IS TOLD WHEN IT OPENS THIS SCREEN: the key, whose it is to hold,
 * and who to ask.
 *
 * Permission keys stay on screen. `kyc.review` is on the permission matrix in this console and a
 * role holds it or does not, so "it needs kyc.review" is an errand somebody can run — quoted to the
 * administrator who grants it. The refusal used to leave the key out ("Your role cannot review
 * identity documents"), on the reasoning that an operator reviews documents and does not hold a
 * key; it sent the reader to an administrator without saying what to ask for. Constraint, table,
 * route and column names are what stay off a screen, and none is here.
 *
 * It takes the role alone, as the funds screen's `alertsNoteWithoutKey` does: the screen decides
 * whether to show it, from the flag it already holds.
 */
export function identityRefusalWithoutKey(role: string): string {
  return `Reviewing identity documents needs kyc.review, which ${role} does not hold. Ask an administrator if you need it.`;
}

/**
 * WHICH "OPEN" IS THE LATEST, for one screen. The console's client has no timeout, and a slow reply
 * to an earlier Open landed whenever it came, replacing the case the reviewer was reading — under
 * "Verify this player" too, which then verified a player whose case nobody had read (final review,
 * 19 Oct 2026). So each Open takes the next number, and a reply is shown only while its number is
 * still the latest; closing the drawer moves the number on as well, so a reply still on its way
 * cannot open a case by itself after the reviewer has closed it.
 *
 * A counter of the screen's own (`useState`, once) rather than a ref: the screen hands `open` to its
 * queue's columns while drawing, and the counter is read only when a reply comes back.
 */
function latestOpenCounter() {
  let latest = 0;
  return {
    /** A new Open, and its number. */
    next: (): number => ++latest,
    /** Whether the reply to Open number `n` is still the one wanted. */
    isLatest: (n: number): boolean => n === latest,
    /** No reply on its way is wanted any more. */
    forgetAll: (): void => {
      latest += 1;
    },
  };
}

export function IdentityChecks() {
  const { allowed, session, brandId, brands } = useSession();
  /*
   * WHETHER THIS SCREEN MAY BE READ AT ALL, decided before anything is fetched.
   *
   * Until 8 October 2026 this was the only routed screen in the console that
   * gated nothing before rendering. `allowed('kyc.review')` appeared once, at
   * the foot of the case drawer, to decide whether to draw the approve button.
   * Routes are not permission-gated — `routes.tsx` mounts the component for any
   * URL typed — so a Finance Lead or an Auditor who opened /identity mounted the
   * screen, fired the queue request, took a 403, and was shown an ERROR DIALOG.
   *
   * A refusal is not an error. Every other module in this console answers "your
   * role does not hold this" as a calm empty state, and this one told them
   * something had gone wrong.
   */
  const canReview = allowed('kyc.review');
  const role = session?.role ?? 'unknown';
  /*
   * THE BRAND CHOSEN IN THE HEADER, which this screen follows as Player funds, Risk and Support's
   * ticket queue do: its waiting list, its count of checks in flight and its decided list are all
   * read for that brand, within the reader's own scope, and for every brand they may see under
   * "All". Until 24 October 2026 it ignored the brand, so with one chosen the dashboard's KYC
   * review count (which follows the filter) counted that brand's cases and its pill opened every
   * brand's.
   *
   * ITS NAME arrives in a second read, after the session's (session.tsx asks for the brands once
   * the session is in), so for a moment — or for good, if that read fails — a chosen brand has no
   * name here. It is then "the selected brand", as Promotions calls it, and never null: null is
   * "All", and the sentences below would describe one brand's list as every brand's.
   */
  const brandName = brandId
    ? (brands.find((b) => b.id === brandId)?.name ?? 'the selected brand')
    : null;
  const { toast } = useToast();
  /* `showUnexpected` rather than `showError`: these are caught values of unknown
     shape, and the convenience is what the rest of the console uses for them. */
  const { showError, showUnexpected } = useErrorDialog();

  /*
   * WHICH LIST IS ON SCREEN, in the address so a link to the decided checks pastes into a
   * ticket. Replaced rather than pushed, as the player profile's tabs are: Back leaves the
   * screen rather than walking back through its tabs.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: IdentityTab = searchParams.get('tab') === 'decided' ? 'decided' : 'waiting';
  /* A case the address names: the player's KYC tab links its current refused check here. */
  const linkedCase = searchParams.get(CASE_PARAM);
  /* Once that case closes — or the link to it is refused, in the pop-up — the address stops naming
     it, so nothing opens it, or says it again, by itself: replaced, so Back does not bring it back
     either. From the address as it is by then, since a refusal arrives after a read. */
  const forgetLinkedCase = () => {
    if (!searchParams.has(CASE_PARAM)) return;
    setSearchParams(
      (now) => {
        const next = new URLSearchParams(now);
        next.delete(CASE_PARAM);
        return next;
      },
      { replace: true },
    );
  };
  const setTab = (key: IdentityTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  const [rows, setRows] = useState<QueueRow[] | null>(null);
  /*
   * WHETHER THE LAST READ OF THE WAITING LIST FAILED. The list is then not known: its figures say
   * "—" and the tab says it did not load, as the Decided tab does — never "Nothing waiting", which
   * is a claim about a list nobody read. A failed read used to leave an empty list behind, and the
   * error dialog sat over "No identity check needs a person right now".
   */
  const [queueFailed, setQueueFailed] = useState(false);
  const [reasons, setReasons] = useState<readonly RefusalReason[]>([]);
  /*
   * Whether an identity service reads these, from the SERVER. Defaults to true so a
   * stale render never claims a deployment has no provider when it has one — the
   * wrong way round of this pair is the one that tells a reviewer to stop trusting a
   * face-match score that is real.
   */
  const [automatic, setAutomatic] = useState(true);
  /*
   * How many checks the service has not finished. ZERO until the server says
   * otherwise, because this number only ever ADDS a sentence — a default of zero
   * cannot invent work that does not exist, and the screen's old behaviour is what
   * zero renders. Its tile is another matter: it says "—" until the list is read,
   * as the figures beside it do — a 0 shown before the read is a number nobody
   * counted.
   */
  const [inFlight, setInFlight] = useState(0);
  /*
   * EVERY CASE THE LIST HOLDS, COUNTED, not only the oldest 200 it shows: the Waiting tile counted
   * the list, so above 200 it stopped at 200 while the dashboard's pill, which counts, went on.
   * Null until read, and from an API older than the counts, when the tiles count the list.
   */
  const [counts, setCounts] = useState<QueueCounts | null>(null);
  /*
   * ONE ERROR DIALOG PER OUTAGE. A failed read of the waiting list opens it; the half-minute reads
   * that fail after it do not — the tab says the list did not load for as long as it does not —
   * until a read works, and a failure after that is a new outage. It reopened every 30 seconds
   * over whatever the reviewer was doing. Kept for the life of the screen, as the counters are.
   */
  const [outage] = useState(() => ({ told: false }));
  /* The decided checks, a page at a time, and whether the read of them came back. */
  const [decided, setDecided] = useState<DecidedList>(DECIDED_UNREAD);
  const [decidedPage, setDecidedPage] = useState(1);
  /*
   * THE CASE IN THE DRAWER, AND WHETHER "VERIFY THIS PLAYER" OR "REFUSE" IS UP OVER IT: one value,
   * so neither dialog can outlive its case or come up over another one.
   *
   * Verify's flag was a value of its own, and Escape came apart between them (18 Oct 2026). The
   * drawer and the dialog both take Escape on the document, the drawer first. Closing the case
   * removed the dialog (it is drawn only while a case is open, below) before the dialog's own
   * handler could run, and on Escape that handler was the only thing that put the flag down. So the
   * next case opened, from either list, came up with "Verify this player" already open: on a case
   * whose name and date were read, one press would have approved a player nobody had chosen to
   * verify. Now closing a case sets this to null, which takes both dialogs with it, and opening one
   * sets it with both closed. Escape still closes a case under Verify, and both with it.
   *
   * REFUSE'S FLAG WAS LEFT A VALUE OF ITS OWN, and outlived its case the same way (final review, 19
   * Oct 2026). Escape under it closed the case and left the dialog up over the queue — it does not
   * close on Escape, below — and in Chromium the drawer then put the keyboard back on the queue's
   * Open behind it: Tab, Tab, Enter opened another case under the still-open dialog, and its Refuse
   * refused THAT case. A slow reply to an earlier Open did the same in both engines. A refusal cannot
   * be undone. So Refuse is in this value too, and while it is up the drawer does not close (its
   * `onClose`, below): Escape does nothing there, and the reason and note typed stay with the case
   * they were typed for. What the boxes of either dialog hold is that dialog's own (`ApproveDialog`,
   * `RefuseDialog`), and starts again with each case.
   */
  /*
   * "ALLOW ONE MORE TRY" IS IN THIS VALUE TOO, for Refuse's reasons: its dialog does not close on
   * Escape either, and allowing one more try cannot be undone. While it is up the drawer does not
   * close, and no reply replaces its case.
   */
  const [inDrawer, setInDrawer] = useState<{
    detail: CaseDetail;
    approving: boolean;
    refusing: boolean;
    allowing: boolean;
  } | null>(null);
  const openCase = inDrawer?.detail ?? null;
  const approving = inDrawer?.approving === true;
  const refusing = inDrawer?.refusing === true;
  const allowing = inDrawer?.allowing === true;
  const setApproving = (up: boolean) =>
    setInDrawer((now) => (now === null ? null : { ...now, approving: up }));
  const setRefusing = (up: boolean) =>
    setInDrawer((now) => (now === null ? null : { ...now, refusing: up }));
  const setAllowing = (up: boolean) =>
    setInDrawer((now) => (now === null ? null : { ...now, allowing: up }));
  /* Which "Open" is the latest, so only its reply is shown (`latestOpenCounter`). Kept for the
     life of the screen, and read only when a reply comes back, never while drawing. */
  const [opens] = useState(latestOpenCounter);
  /* The same counter for each list's reads: only the latest read's reply is drawn, so a reply for a
     brand the reader has left — or one overtaken by a later read — never lands on the list. */
  const [queueReads] = useState(latestOpenCounter);
  const [decidedReads] = useState(latestOpenCounter);
  /* The photographs revealed on the open case live in IdentityPhotos, keyed by the case, so a
     signed link never outlives the case it was minted for. */
  const [busy, setBusy] = useState(false);

  /*
   * A BRAND CHANGE TAKES THE OLD BRAND'S LISTS DOWN AT ONCE. Until the new brand's reads land, the
   * figures say "—" and the lists say nothing, rather than showing another brand's cases under the
   * new name; a read that failed for the old brand is not said to have failed for the new one; the
   * decided list starts again at its first page; and any read still on its way for the old brand is
   * forgotten. Adjusted during render, as the payout queue does on a brand change, so the new brand
   * is read once, from its first page.
   */
  const [listedBrand, setListedBrand] = useState(brandId);
  if (listedBrand !== brandId) {
    setListedBrand(brandId);
    setRows(null);
    setQueueFailed(false);
    setInFlight(0);
    setCounts(null);
    setDecided(DECIDED_UNREAD);
    setDecidedPage(1);
    queueReads.forgetAll();
    decidedReads.forgetAll();
  }

  const load = useCallback(async () => {
    /* Guarded as well as gated. The early return below stops the screen
       RENDERING, but an effect already scheduled would still fetch — and a 403
       from a background poll opens the error dialog over whatever the operator
       navigated to next. `capability-gate.test.ts` exists for exactly this
       half. */
    if (!canReview) return;
    const mine = queueReads.next();
    try {
      const data = await api.get<{
        cases: QueueRow[];
        refusalReasons: RefusalReason[];
        /* Whether an identity service reads these at all, which decides what this
           queue IS — the exceptions a service could not finish, or every check on
           the platform. The heading said the first on a deployment that is the
           second. */
        checksAreAutomatic: boolean;
        /* Checks the service is still working on — in neither the queue nor done. */
        inFlight: number;
        /* Every case the list holds, beside the oldest 200 it sends. */
        counts?: QueueCounts;
      }>(
        brandId
          ? `/api/kyc/identity/queue?brandId=${encodeURIComponent(brandId)}`
          : '/api/kyc/identity/queue',
      );
      if (!queueReads.isLatest(mine)) return;
      setRows(data.cases);
      setReasons(data.refusalReasons);
      setAutomatic(data.checksAreAutomatic);
      setInFlight(data.inFlight ?? 0);
      setCounts(data.counts ?? null);
      setQueueFailed(false);
      outage.told = false;
    } catch (error) {
      if (!queueReads.isLatest(mine)) return;
      /* Not an empty list: a list that did not load (`queueFailed`). */
      setRows(null);
      setQueueFailed(true);
      if (!outage.told) {
        outage.told = true;
        showUnexpected(error, 'Could not load the identity queue');
      }
    }
  }, [showUnexpected, canReview, brandId, queueReads, outage]);

  /* The decided checks, guarded exactly as `load` is: the refusal below is a render, and an
     effect has already fired by the time it paints. */
  const loadDecided = useCallback(async () => {
    if (!canReview) return;
    const mine = decidedReads.next();
    /* A failed read opens the dialog and leaves the tab saying it did not load: never an empty
       list, which the tab would call "nothing decided yet". Neither for a read no longer wanted. */
    const list = await settleDecided(
      api.get<{ items: DecidedRow[]; meta: PageMeta }>(
        `/api/kyc/identity/decided?page=${decidedPage}&perPage=${DECIDED_PER_PAGE}` +
          (brandId ? `&brandId=${encodeURIComponent(brandId)}` : ''),
      ),
      (error) => {
        if (decidedReads.isLatest(mine)) showUnexpected(error, 'Could not load the decided checks');
      },
    );
    if (decidedReads.isLatest(mine)) setDecided(list);
  }, [showUnexpected, canReview, decidedPage, brandId, decidedReads]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Read when its tab is open, and again for each page. */
  useEffect(() => {
    if (tab === 'decided') void loadDecided();
  }, [tab, loadDecided]);

  /*
   * ── THE QUEUE LOOKS AGAIN ─────────────────────────────────────────────────
   *
   * It loaded once, on mount, and never again. A reviewer who opened this screen
   * while a check was in flight sat in front of a list that was true when it
   * rendered and stale a minute later, with nothing on the page to suggest it.
   *
   * MEASURED on a real verification: submitted 13:44, captured 13:45, settled
   * 13:51. The reviewer signed in at 13:47 and was shown "Nothing waiting" — four
   * minutes before a case arrived, with no reason to press anything.
   *
   * THIRTY SECONDS, not one: this is a work queue a person watches for minutes at a
   * time, not a dealing screen. And the interval is cleared on unmount, or a
   * reviewer who navigates away leaves a timer fetching another brand's scope
   * behind them — and when the header's brand changes, since `load` reads the
   * brand on screen: the old timer goes and the new one reads the new brand.
   *
   * NOT WHILE A CASE IS OPEN. Replacing the rows under a reviewer reading a
   * document is how the row they are about to press moves — and `openCase` holds
   * its own copy, so the drawer itself does not need the refresh.
   *
   * AND ONLY ON THE QUEUE'S OWN TAB. The decided list is a record of work done, not
   * work arriving; it is read when its tab opens and when its Refresh is pressed.
   */
  useEffect(() => {
    if (openCase || !canReview || tab !== 'waiting') return;
    const timer = window.setInterval(() => void load(), QUEUE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load, openCase, canReview, tab]);

  /* One case in full — from either list. A signed photograph link belongs to one record,
     so the drawer's photographs are keyed by the case and start unrevealed each time.
     ONLY THE LATEST OPEN'S REPLY COUNTS (`opens`), its failure included: a reviewer reading
     one case is not told an earlier read failed. And NO REPLY REPLACES A CASE WHILE ONE OF ITS
     DIALOGS IS UP: the dialog stays for the case it was pressed on. The backdrop keeps the mouse
     off the lists meanwhile, but not a keyboard Chromium put back on them, nor a reply.
     Answers whether the case's refusal was what the pop-up showed. */
  const open = useCallback(
    async (id: string): Promise<boolean> => {
      /* Guarded here as `load` is: the address can name a case (below), and an effect opens it
         whatever the screen painted. */
      if (!canReview) return false;
      const mine = opens.next();
      try {
        const detail = await api.get<CaseDetail>(`/api/kyc/identity/${encodeURIComponent(id)}`);
        if (!opens.isLatest(mine)) return false;
        setInDrawer((now) =>
          now !== null && (now.approving || now.refusing || now.allowing)
            ? now
            : { detail, approving: false, refusing: false, allowing: false },
        );
        return false;
      } catch (error) {
        if (!opens.isLatest(mine)) return false;
        showUnexpected(error, 'Could not open that case');
        return true;
      }
    },
    [canReview, opens, showUnexpected, setInDrawer],
  );

  /*
   * A CASE THE ADDRESS NAMES, opened as the reviewer's own Open would: the player's KYC tab links its
   * current refused check here (`identityCaseHref`), where support may allow one more try. The case
   * route decides the reader's brand scope, so a case outside it, or none at all, is answered by its
   * refusal in the error pop-up. A reader without kyc.review reads nothing: `open` is guarded itself,
   * because the refusal below is a render and this effect would already have fired.
   *
   * ONLY AN ID THE DATABASE COULD HAVE WRITTEN IS ASKED FOR (`namesAnIdentityCheck`). Anything else
   * opens nothing and asks for nothing, and the reader is told the link names no check — never a
   * reader without kyc.review, who is shown the screen's refusal instead.
   *
   * EITHER REFUSAL IS SAID ONCE: the link then leaves the address (`forgetLinkedCase`, replaced),
   * so a reload or Back does not open the same pop-up again.
   */
  useEffect(() => {
    if (linkedCase === null) return;
    if (namesAnIdentityCheck(linkedCase)) {
      void open(linkedCase).then((refused) => {
        if (refused) forgetLinkedCase();
      });
      return;
    }
    if (canReview) {
      showError({ title: 'Could not open that case', message: LINK_NAMES_NO_CHECK });
      forgetLinkedCase();
    }
    // `forgetLinkedCase` is drawn anew each render and reads the address as it is when called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, linkedCase, open, showError]);

  /* Revealing a photograph — one press, one recorded access, one link minted at the moment
     of the click — is IdentityPhotos', which the player's KYC tab shares. */

  const decide = async (body: Record<string, unknown>) => {
    if (!openCase || busy) return;
    setBusy(true);
    try {
      const result = await api.post<VerdictAnswer>(
        `/api/kyc/identity/${encodeURIComponent(openCase.id)}/verdict`,
        body,
      );
      toast(verdictToast(result));
      setInDrawer(null);
      forgetLinkedCase();
      await load();
    } catch (error) {
      showUnexpected(error, 'That verdict was not saved');
    } finally {
      setBusy(false);
    }
  };

  /*
   * ONE MORE TRY, for the case it was pressed on. The case stays open and is read again, so it shows
   * who allowed it, when and why — through `open`, once the dialog is down, so only that read lands.
   *
   * REFUSED — a colleague allowed it first, say — or failed: the dialog goes, the reason is shown in
   * the error pop-up, and the case is read again, so no press is left standing on a case that no
   * longer offers it.
   */
  const allowOneMoreTry = async (reason: string) => {
    if (!openCase || busy) return;
    setBusy(true);
    try {
      const result = await api.post<{ playerRef: string; emailed?: boolean }>(
        `/api/kyc/identity/${encodeURIComponent(openCase.id)}/one-more-try`,
        { reason },
      );
      toast(oneMoreTryToast(result.playerRef, result.emailed === true));
      setAllowing(false);
      await open(openCase.id);
    } catch (error) {
      setAllowing(false);
      showUnexpected(error, 'One more try was not allowed');
      await open(openCase.id);
    } finally {
      setBusy(false);
    }
  };

  const columns = queueColumns((id) => void open(id));

  /* Which fields the reviewer must supply, and the boxes they type them into, are the approve
     dialog's (`ApproveDialog`, below); the case's alerts and its date-of-birth comparison are
     IdentityCase's. */

  /*
   * The refusal, at the component's own top level and in the shape
   * `permission-gates.test.ts` reads: anchored at two spaces, on a flag it can
   * resolve back to `allowed('kyc.review')`. Deeper or differently shaped, the
   * scan records no gate for this screen and the comparison it performs is
   * against nothing.
   *
   * The sentence names the key, the reader's role and who to ask, because
   * permission keys stay on screen: a role holds `kyc.review` or does not, and
   * "ask for kyc.review" is an errand somebody can run. What stays off a screen
   * is a schema (a constraint, a table, a route), which nobody can act on.
   */
  if (!canReview) {
    return (
      <EmptyState icon="lock-key" title="Identity checks">
        {identityRefusalWithoutKey(role)}
      </EmptyState>
    );
  }

  return (
    <>
      <ScreenHeader
        title="Identity checks"
        /*
          THE SENTENCE WAS ALREADY IN THIS FILE, twice over, and an operator read
          neither half of it.

          The module header above says the queue is people and that an account
          does nothing until somebody decides it; the paragraph this header
          replaces said the same thing inside the panel, under the figures. The
          consequence is not a flourish either: an unverified account cannot
          deposit, play or withdraw from the moment it is opened — enforced in
          both directions by `assertIdentityVerified` and `playerBarredFromPlay`,
          not merely implied here — so a case left sitting in this queue for a
          week is a customer who has been able to do nothing for a week. That is
          the fact a reviewer arriving on shift most needs, so it goes first.
        */
        purpose="The queue of identity checks waiting on a person, worked by whoever is on shift: until one is decided, that player cannot deposit or play. Decided checks are kept here too, read-only, to show how each player was verified."
        /*
          WHAT THIS QUEUE IS TODAY — the half of the old paragraph that could not
          become `purpose`, kept as the derived line it always was.

          `automatic` comes from the server and decides what the list in front of
          the reviewer actually CONTAINS: where an identity service runs these are
          the checks it could not verify on its own (since 15 Oct 2026 that is
          every new one: CIRA screens nobody on this account, and the automatic
          branch's sentence, `identityQueueWords`, now says so), and where none is
          configured they are every check on the platform, with the reviewer as
          the only check there is. The module header explains why the wrong way
          round of that pair is the expensive one — it tells a reviewer either to
          distrust a face-match score that is real, or to trust one that was never
          taken — so both branches survive here, minus the one clause noted below.

          It belongs in `meta` rather than `purpose` because `purpose` is fixed
          prose about the screen's job while `meta` is what the screen currently
          knows, and this flips with the deployment rather than with the screen.
          Keeping `purpose` a plain literal matters for a second reason:
          `screen-purpose.test.ts` can only read a literal, and a conditional
          there would quietly make this the first screen whose sentence escapes
          that check while still appearing to be covered by it.

          The clause both branches used to end with — "the account does nothing
          until it is decided" — is not repeated here, because the purpose above
          now says it in stronger words. Nothing else was dropped.
        */
        meta={
          /* And on the Decided tab, what that list is. */
          tab === 'decided'
            ? 'Verified and refused checks, the newest decision first. Each opens read-only: how it was decided, what the check found, and the photographs.'
            : automatic
              ? identityQueueWords().automatic
              : 'No identity service is configured, so every check on this platform is read here by a person. Nothing is wrong with these photographs — you are the check.'
        }
        /* The refresh the panel head used to carry, for whichever list is open. The
           queue also looks again on its own every half a minute — the effect above
           does it, and the empty state for a check still in flight says so out loud
           — so this button is for a reviewer who does not want to wait, not the
           only way the list ever moves. */
        actions={
          <Button
            variant="secondary"
            onClick={() => void (tab === 'decided' ? loadDecided() : load())}
          >
            <Icon name="arrows-clockwise" /> Refresh
          </Button>
        }
      />

      <Tabs
        tabs={IDENTITY_TABS}
        active={tab}
        onChange={setTab}
        label="Identity checks: waiting or decided"
      />

      {tab === 'waiting' ? (
        /* The panel keeps the figures and the queue and loses its own head: the
           name and the refresh moved up into the screen header, and a second
           "Identity checks" heading directly beneath the first would leave an
           operator working out which of the two the table belongs to. */
        <Panel>
          {/* `.stat-grid`, as every other screen's tiles use, and NOT a bare flex
              row. A `.stat` sizes itself from its container, so in a flex row
              with no basis each tile shrank to its label's longest word — three
              tall slivers with "OVER 3 DAYS" wrapped one word a line and
              "WAITING" clipped. The grid gives each tile a real column. */}
          <div className="stat-grid" style={{ marginBottom: 'var(--space-6)' }}>
            <Stat
              label="Waiting"
              value={rows === null ? '—' : formatCount(counts?.waiting ?? rows.length)}
            />
            <Stat
              label="Over 3 days"
              value={
                rows === null
                  ? '—'
                  : formatCount(
                      counts?.overThreeDays ?? rows.filter((r) => r.waitingDays >= 3).length,
                    )
              }
            />
            <Stat
              label="Nothing read"
              value={
                rows === null
                  ? '—'
                  : formatCount(
                      counts?.nothingRead ?? rows.filter((r) => !r.hasExtractedIdentity).length,
                    )
              }
            />
            {/* Only where a service runs them. With no provider there is nothing in
                flight by definition, and a tile reading 0 would imply there could be. */}
            {automatic ? (
              <Stat
                label="Still being checked"
                value={rows === null ? '—' : formatCount(inFlight)}
              />
            ) : null}
          </div>

          {queueFailed ? (
            <EmptyState icon="warning-circle" title="The waiting checks did not load">
              The message that opened says why. This does not mean nothing is waiting: the list
              looks again every half a minute, or press Refresh to read it now.
            </EmptyState>
          ) : rows !== null && rows.length === 0 ? (
            /*
             * TWO DIFFERENT FACTS, and this said the first while the second was true.
             *
             * An empty queue with nothing in flight means the work is done. An empty
             * queue with a check still running means a case is coming and the reviewer
             * should not leave. "No identity check needs a person right now" was read
             * by somebody four minutes before one did.
             */
            inFlight > 0 ? (
              <EmptyState icon="clock" title="Nothing waiting yet">
                {identityQueueWords(brandName).inFlight(inFlight)}
              </EmptyState>
            ) : (
              <EmptyState icon="check-circle" title="Nothing waiting">
                {identityQueueWords(brandName).nothingWaiting}
              </EmptyState>
            )
          ) : (
            <>
              <DataTable
                rows={rows ?? []}
                columns={columns}
                rowKey={(r) => r.id}
                caption="Identity checks waiting for a person, with the player, why it needs review, the face-match score, how many photographs are held and how long it has waited"
              />
              {/* The list is the oldest 200 (the route's own limit): said where the count is more,
                  so the Waiting figure above it is not read as the rows below. */}
              {rows !== null && counts !== null && counts.waiting > rows.length && (
                <p className="text-subtle" style={{ marginTop: 'var(--space-3)', marginBottom: 0 }}>
                  The oldest {formatCount(rows.length)} of {formatCount(counts.waiting)} are listed
                  here. The others join the list as these are decided.
                </p>
              )}
            </>
          )}
        </Panel>
      ) : (
        /* THE CHECKS ALREADY DECIDED, newest decision first — each opens in the same
           case view, read-only. Paged rather than capped, so none is out of reach. */
        <Panel>
          <DecidedChecks
            list={decided}
            page={decidedPage}
            onOpen={(id) => void open(id)}
            onPage={setDecidedPage}
            brandName={brandName}
          />
        </Panel>
      )}

      {/* NOT CLOSED UNDER "REFUSE" OR "ALLOW ONE MORE TRY". The drawer takes Escape before any
          dialog over it, and neither closes on Escape, so closing here left the dialog up over the
          queue (`inDrawer`). A close still forgets every reply on its way, so none opens a case by
          itself. */}
      <Drawer
        open={openCase !== null}
        onClose={() => {
          opens.forgetAll();
          setInDrawer((now) => (now?.refusing === true || now?.allowing === true ? now : null));
          if (!refusing && !allowing) forgetLinkedCase();
        }}
        title={openCase ? `${openCase.playerRef} · identity check` : ''}
      >
        {openCase && (
          <IdentityCase
            c={openCase}
            busy={busy}
            onVerify={() => setApproving(true)}
            onRefuse={() => setRefusing(true)}
            onAllowOneMoreTry={() => setAllowing(true)}
          />
        )}
      </Drawer>

      {/* ── Approving ────────────────────────────────────────────────────────
          One per case (`key`), and up only while its case is open (`inDrawer`, above): its
          boxes start from that case's reading, and the next case opens a fresh one, closed.
          The key names the case AND the dialog. The refuse dialog below is its sibling, and React
          keeps one element per key among siblings: with the case's id alone on both, closing a
          case removed only the refuse dialog and left "Verify PL-…" on screen over the queue —
          its Verify still working after Escape, both buttons dead after a saved Verify (22 Oct
          2026; sibling-keys-differ.test.ts). */}
      {openCase && (
        <ApproveDialog
          key={`${openCase.id}:approve`}
          c={openCase}
          open={approving}
          busy={busy}
          onClose={() => setApproving(false)}
          onVerify={(body) => void decide(body)}
        />
      )}

      {/* ── Refusing ───────────────────────────────────────────────────────
          One per case (`key`), like the approve dialog, and up only while its case is open
          (`inDrawer`, above): the reason and the note start empty with each case. Keyed apart
          from the approve dialog — the case's id and which dialog this is — for the reason given
          there: two siblings under one key, and React removes only one of them. */}
      {openCase && (
        <RefuseDialog
          key={`${openCase.id}:refuse`}
          c={openCase}
          open={refusing}
          busy={busy}
          reasons={reasons}
          onClose={() => setRefusing(false)}
          onRefuse={(body) => void decide(body)}
        />
      )}

      {/* ── One more try ────────────────────────────────────────────────────
          One per case (`key`), like the verdict dialogs, and up only while its case is open: the
          reason starts empty with each case. Keyed apart from both of them, for the reason given at
          the approve dialog. */}
      {openCase && (
        <AllowOneMoreTryDialog
          key={`${openCase.id}:one-more-try`}
          c={openCase}
          open={allowing}
          busy={busy}
          onClose={() => setAllowing(false)}
          onAllow={(reason) => void allowOneMoreTry(reason)}
        />
      )}
    </>
  );
}

/**
 * THE REFUSE DIALOG, for one case — what refusing costs, the reason from the closed set, what the
 * reviewer saw, and the Refuse press. Its boxes are its own state, mounted once per case (`key` at the
 * call), so a reviewer moving to the next case never carries the last one's reason or note with them;
 * and its title names the player, as the approve dialog's does, because the case behind a dialog is
 * blurred past reading. Exported so a test can render it as the case opens it.
 */
export function RefuseDialog({
  c,
  open,
  busy,
  reasons,
  onClose,
  onRefuse,
}: {
  c: CaseDetail;
  open: boolean;
  busy: boolean;
  reasons: readonly RefusalReason[];
  onClose: () => void;
  onRefuse: (body: Record<string, unknown>) => void;
}) {
  const [refusalReason, setRefusalReason] = useState('');
  const [refusalNote, setRefusalNote] = useState('');
  /* The reason chosen, as the routes sent it, with whether the player can put it right. */
  const chosen = reasons.find((r) => r.key === refusalReason) ?? null;

  return (
    <>
      {/* NOT DISMISSIBLE, AND NOT BECAUSE A WRITE IS IN FLIGHT. That side is
          already guarded: both buttons disable themselves on `busy` while the
          verdict is in the air. The moment worth guarding is the one BEFORE the
          press, where a reviewer has chosen a reason from the closed set and
          typed at least ten characters of what they actually saw on the
          document — which is the only account of this decision a support agent
          on the phone to this player will ever have. A stray Escape, or a click
          landing a few pixels outside this panel, threw all of that away without
          a word, and the case went back to the queue for the next reviewer to
          work out from the photographs again. Cancel is still one click, so the
          way out has not gone; it has only stopped being an accident. (Escape
          reaches the drawer under this dialog first, and the drawer does not
          close while this is up — `IdentityChecks`.) */}
      <Dialog
        open={open}
        onClose={onClose}
        title={`Refuse ${c.playerRef}’s identity check`}
        dismissible={false}
        actions={
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                onRefuse({
                  outcome: 'REFUSED',
                  reason: refusalReason,
                  note: refusalNote.trim(),
                })
              }
              disabled={busy || !refusalReason || refusalNote.trim().length < 10}
            >
              Refuse
            </Button>
          </>
        }
      >
        {/* DANGER RATHER THAN WARN, AND IT IS NOT A CLOSE CALL. The tone
            contract written beside `.notice-warn` in the design system keeps
            danger for the irreversible, and a refusal is as irreversible as
            anything this console does. The verdict is accepted only while the
            case is still with a person, so a second press on this one is
            answered "already settled", and no control a reviewer can reach
            turns a refusal back into a check waiting to be decided. Their
            account's KYC standing moves to rejected in the same transaction
            that records the verdict — that is what keeps deposits, play and
            withdrawals closed — and the message to the player is recorded in
            that transaction too, so by the time the toast appears they have
            been told.

            WHAT THE PLAYER MAY DO NEXT IS THE REASON'S, and the tries'. For a
            reason they can put right, while this refusal leaves them fewer than
            three refused checks in all, their screen says what to fix and takes
            new photographs — a new check, which a person decides; never this
            one reopened. Otherwise it tells them to raise a ticket. The notice
            says which once a reason is chosen (`RefusalNotice`), counting this
            refusal, so the reviewer reads it before the press rather than from
            the player's next support ticket; the list marks which reasons let
            them try again, and the hint under it names the reason for somebody
            else's ID. */}
        <div className="notice notice-danger">
          <Icon name="warning" />
          <RefusalNotice reason={chosen} refusalsSoFar={c.refusalsSoFar} />
        </div>
        <Field label="Reason" hint={REASON_HINT}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={refusalReason}
              onChange={(e) => setRefusalReason(e.target.value)}
            >
              <option value="">Choose one</option>
              {reasonGroups(reasons, c.refusalsSoFar).map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.reasons.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="What you saw — for the next reviewer, not the player"
          hint="At least ten characters. A support agent on the phone to this player will have only this."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={refusalNote}
              onChange={(e) => setRefusalNote(e.target.value)}
              placeholder="The ID photo is cropped and the number is cut off"
            />
          )}
        </Field>
        <p className="text-subtle">
          A support agent on the phone to this player will have only this note. “Document
          unreadable” does not say whether the photo was blurred, cropped or a picture of a screen.
        </p>
      </Dialog>
    </>
  );
}

/**
 * THE APPROVE DIALOG, for one case — what approving does, the boxes for what the service did not read
 * (`ApprovalTyping`), a note, and the Verify press.
 *
 * ITS BOXES ARE ITS OWN STATE, opened from the case: the ID's reading, or nothing. Never the name the
 * player gave at sign-up, and never the account's date of birth — item 21 asks for what the ID
 * presented shows, which is a person reading the ID. Mounted once per case (`key` at the call), so a
 * reviewer moving to the next case never carries the last one's typing with them. Its title names the
 * player ("Verify PL-…"): the case behind it is blurred past reading, and "Verify this player" could
 * not show a reviewer that the case under it was not the one they had read (final review, 19 Oct
 * 2026). Exported so a test can render the dialog as the case opens it.
 *
 * ── WHICH FIELDS THE REVIEWER MUST SUPPLY, one per field rather than one for both ──
 *
 * This was a single boolean keyed on the NAME, while the verdict route demands a name AND a date of
 * birth. So a case where the service read the name and found no date of birth on that layout — a
 * driving licence, which is common — offered no field to type one into, and the route refused the
 * approval with "approving it needs the date of birth on the ID. Type what the ID shows." There was
 * nowhere to type it. The reviewer could not approve the case and could not see why. Found on the
 * first real case through this screen.
 *
 * `record.date_of_birth` is what the route checks, which is `document.dateOfBirth` here — not the
 * player's declared one, which is never a substitute for what the ID shows.
 */
export function ApproveDialog({
  c,
  open,
  busy,
  onClose,
  onVerify,
}: {
  c: CaseDetail;
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onVerify: (body: Record<string, unknown>) => void;
}) {
  const [typedName, setTypedName] = useState(c.document.fullName ?? '');
  const [typedDob, setTypedDob] = useState(c.document.dateOfBirth ?? '');
  const [note, setNote] = useState('');
  const needsName = c.document.fullName === null;
  const needsDob = c.document.dateOfBirth === null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Verify ${c.playerRef}`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onVerify({
                outcome: 'VERIFIED',
                /* Only what was asked for. Sending an empty `fullName` on a case whose
                   name WAS read would overwrite the document's reading with nothing. */
                ...(needsName ? { fullName: typedName } : {}),
                ...(needsDob ? { dateOfBirth: typedDob } : {}),
                ...(note.trim() ? { note: note.trim() } : {}),
              })
            }
            disabled={busy || (needsName && typedName.trim().length < 2) || (needsDob && !typedDob)}
          >
            Verify
          </Button>
        </>
      }
    >
      <p>
        This opens the account: the player can deposit, play and withdraw. They are emailed straight
        away, if we can write to their address.
      </p>

      <ApprovalTyping
        c={c}
        typedName={typedName}
        onTypedName={setTypedName}
        typedDob={typedDob}
        onTypedDob={setTypedDob}
      />

      <Field
        label="Note for the next reviewer (optional)"
        hint="Recorded on the audit trail. Never shown to the player."
      >
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What you checked"
          />
        )}
      </Field>
    </Dialog>
  );
}

/**
 * WHAT THE APPROVE DIALOG ASKS THE REVIEWER TO TYPE, and only when the service did not read it: the
 * name exactly as the ID prints it, the date of birth on the ID, or both. Nothing at all on a case
 * whose name and date were read.
 *
 * THE NAME GIVEN AT SIGN-UP SITS ABOVE THE NAME BOX, as reference — the player said the ID prints
 * it so, and a reviewer reading the photograph can see where the two part. It never fills the box
 * (the screen opens a case with the box holding the ID's reading, or nothing): item 21 asks for the
 * name "as it appears in the ID presented", which is a person reading the ID, not a person agreeing
 * with what the player typed.
 *
 * Exported so the dialog's boxes can be rendered in a test without opening a dialog.
 */
export function ApprovalTyping({
  c,
  typedName,
  onTypedName,
  typedDob,
  onTypedDob,
}: {
  c: CaseDetail;
  typedName: string;
  onTypedName: (value: string) => void;
  typedDob: string;
  onTypedDob: (value: string) => void;
}) {
  const needsName = c.document.fullName === null;
  const needsDob = c.document.dateOfBirth === null;
  if (!needsName && !needsDob) return null;
  /* The name given at sign-up, and — where a correction after a refusal changed it — the corrected
     one under it, as the comparison row shows them. Neither fills the box. */
  const corrected = nameWasCorrected(c.declared) ? c.declared.correction : null;
  const signUpName = c.declared.nameGivenAtSignUp
    ? corrected !== null
      ? corrected.signUpName
      : c.declared.fullName
    : null;

  return (
    <>
      <p className="text-subtle">
        {/* NAMES WHAT IS MISSING. "The service could not read this document" is
            false on a case where it read seven fields and missed one, and it sends
            a reviewer looking for a problem with the photograph. */}
        {needsName && needsDob
          ? 'The service read neither the name nor the date of birth off this document. ' +
            'PAGCOR requires both, so type what the ID shows.'
          : needsName
            ? 'The service did not read a name off this document. PAGCOR requires it, so ' +
              'type what the ID shows.'
            : 'The service read this document but found no date of birth on it — common on ' +
              'a licence. PAGCOR requires one, so type what the ID shows.'}
      </p>
      {needsName && (signUpName !== null || corrected !== null) && (
        <dl style={{ margin: 0, marginBottom: 'var(--space-3)' }}>
          <div className="def-row">
            {/* The muted text token, not the row's subtle one: on the dialog's glass the subtle grey
                measured 4.09:1 at worst in dark, under the 4.5:1 floor. A class on the <dt> loses to
                `.def-row dt`, so the label carries its own. */}
            <dt>
              <span className="text-muted">
                {signUpName !== null
                  ? 'Name given at sign-up'
                  : 'Display name (no name was asked at sign-up)'}
              </span>
            </dt>
            <dd style={{ minWidth: 0 }}>
              <PersonName>{signUpName !== null ? asOnId(signUpName) : c.playerName}</PersonName>
              {corrected !== null && (
                <CorrectedAfterRefusal day={calendarDay(corrected.correctedOn)}>
                  <PersonName>{c.declared.fullName === null ? null : asOnId(c.declared.fullName)}</PersonName>
                </CorrectedAfterRefusal>
              )}
            </dd>
          </div>
        </dl>
      )}
      {needsName && (
        <Field
          label="Full name, exactly as the ID prints it"
          hint={
            corrected !== null
              ? 'Type it exactly as the ID prints it, even where it differs from the names the player gave.'
              : signUpName !== null
                ? 'Type it exactly as the ID prints it, even where it differs from the name given at sign-up.'
                : 'Item 21 asks for the name as it appears in the ID presented — not what the player typed at registration.'
          }
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={typedName}
              onChange={(e) => onTypedName(e.target.value)}
              placeholder="MARIA REYES SANTOS"
            />
          )}
        </Field>
      )}
      {needsDob && (
        <Field
          label="Date of birth on the ID"
          hint="What the DOCUMENT shows, not what the player typed at registration — those are two claims and this records the first."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              value={typedDob}
              onChange={(e) => onTypedDob(e.target.value)}
            />
          )}
        </Field>
      )}
    </>
  );
}

/**
 * ONE CASE, IN FULL — the drawer on Identity checks, for a check waiting for a person and for
 * one already decided.
 *
 * ── A DECIDED CHECK OPENS IN THE SAME VIEW, READ-ONLY ───────────────────────
 *
 * The Decided tab opens this exactly as the queue does, and three things change with it:
 *
 *   HOW IT WAS DECIDED comes first — who decided and when, their note, and whether the name
 *   and the date of birth are the document's reading or the reviewer's typing.
 *
 *   THE FINDINGS STAY AND THE ADVICE GOES. The alerts are facts about the check; the sentence
 *   under each is advice to somebody about to decide.
 *
 *   NO VERDICT BUTTONS. `verdictIsOpen` decides, by the rule the verdict route enforces.
 *
 * Exported so both halves can be rendered in a test without opening a drawer.
 */
export function IdentityCase({
  c,
  busy,
  onVerify,
  onRefuse,
  onAllowOneMoreTry,
}: {
  c: CaseDetail;
  busy: boolean;
  onVerify: () => void;
  onRefuse: () => void;
  onAllowOneMoreTry: () => void;
}) {
  const decidable = verdictIsOpen(c);
  /* The platform rules refuse to approve it, so Verify is closed before anybody types. */
  const noCiraCheck = noCiraCheckOnRecord(c);
  /* `needsName`, not `needsTyping` — see the panel that reads it. */
  const needsName = c.document.fullName === null;
  /* Recomputed per render from the case, which is a handful of comparisons — no
     memo, because a stale alert list is exactly the kind of thing that goes unnoticed. */
  const alerts = alertsFor(c);
  const dobDisagrees =
    c.document.dateOfBirth !== null &&
    c.declared.dateOfBirth !== null &&
    c.document.dateOfBirth !== c.declared.dateOfBirth;
  /* A date of birth corrected after this check: a later fact, said under the sentence about the one
     the check was sent. */
  const laterCorrection = laterCorrectionOf(c);
  const laterDateOfBirth = laterCorrection?.dateOfBirth ?? null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      {/* ── HOW IT WAS DECIDED, on a decided check: the record the player's KYC tab
             shows, less the liveness and the confidences this view shows below. */}
      {c.decision !== null && (
        <Panel title="How this was decided">
          <RecordFactsList record={factsOfCase(c)} evidence={false} />
        </Panel>
      )}

      {/* ── SUPPORT'S ONE MORE TRY, once allowed after this refusal: who, when and why. On the
             refusal it answers, current or replaced by the try it opened. */}
      {c.oneMoreTry?.allowed && <OneMoreTryAllowed allowed={c.oneMoreTry.allowed} />}

      {/* ── WHAT TO LOOK AT FIRST ─────────────────────────────────
             Ranked, and each one says what it MEANS for the decision rather than
             only what it is. A reviewer reading "face match 0.71" has to know the
             floor to know whether to care; a reviewer reading "below the 0.80
             floor this platform requires" does not.

             ON A CHECK THAT CANNOT BE DECIDED the findings stay and the advice goes:
             each "means" is written for somebody about to decide, and on a decided
             check it would tell a reader to do what has already been done. */}
      <Panel title={decidable ? 'Needs your attention' : 'What the check found'}>
        {alerts.length === 0 ? (
          <p className="text-subtle" style={{ marginTop: 0 }}>
            {decidable
              ? 'Nothing here is flagged. The service could not finish for the reason in the note below — read it, then look at the photographs.'
              : 'The check flagged nothing.'}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {alerts.map((a) => (
              <div
                key={a.what}
                className={a.weight === 'stop' ? 'notice' : 'notice-quiet'}
                style={{ alignItems: 'flex-start' }}
              >
                <Icon name={a.icon} />
                <div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <strong>{a.what}</strong>
                    {decidable && <Tag tone={alertTone(a.weight)}>{ALERT_WORD[a.weight]}</Tag>}
                  </div>
                  {/* The muted text token: the subtle grey measured 3.65:1 in dark on a "Decide
                      on this" finding's tint (3.22:1 at worst), under the 4.5:1 floor. */}
                  {decidable && (
                    <div className="text-muted" style={{ marginTop: 'var(--space-1)' }}>
                      {a.means}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* The provider's own summary, kept because a reviewer who disagrees with
            the alerts above needs to see what the service actually said. On a check
            that cannot be decided the note is the decider's, in the panel above. */}
        {decidable && (
          <p style={{ marginTop: 'var(--space-4)', marginBottom: 0 }}>
            {c.reviewNote ?? 'Sent for review.'}
          </p>
        )}
        {/* WHAT CIRA SAID AFTER THE CHECK CAME HERE — its analyst's decision on a PEP match,
            most often (CIRA, 15 Oct 2026). Beside the first answer above, never in place of it,
            and deciding nothing: the case is still a person's. On a decided check the record at
            the top shows it beside the decision instead. */}
        {c.decision === null && c.providerSaid.laterAnswer !== null && (
          <div
            className="notice notice-quiet"
            style={{ marginTop: 'var(--space-4)', alignItems: 'flex-start' }}
          >
            <Icon name="info" />
            <div>
              {/* "Again" only where CIRA had answered before: a player may ask for a person
                  before CIRA answers at all, and its first answer then comes after (the case says
                  which). */}
              <strong>
                CIRA answered{c.providerSaid.answeredBefore === true ? ' again' : ''} on{' '}
                {momentWords(c.providerSaid.laterAnswer.at)}
              </strong>
              <div className="text-subtle">{laterAnswerWords(c.providerSaid.laterAnswer)}</div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
          {c.providerStatus && (
            <Tag tone="neutral">Service said: {c.providerStatus}</Tag>
          )}
          {c.biometric.faceMatchScore !== null && (
            <Tag tone={confidenceTone(c.biometric.faceMatchScore)}>
              Face match {faceMatchWords(c.biometric.faceMatchScore)}
            </Tag>
          )}
          {c.biometric.liveness && (
            <Tag tone={c.biometric.liveness.toLowerCase() === 'passed' ? 'quiet' : 'accent'}>
              Liveness {c.biometric.liveness}
            </Tag>
          )}
        </div>
      </Panel>

      {/* ── THE COMPARISON THAT IS THE REVIEW ──────────────────────
             The account's claim beside the document's, so a reviewer can say
             whether they agree without opening the player's profile in another tab.
             The screen used to show the document alone and one line about the date
             of birth, which made the comparison something a reviewer did from
             memory. */}
      <Panel title="The document against the account">
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {COMPARISONS.map(({ label, declared, document: docKey, verdict }) => {
            const raw = c.document[docKey];
            /* The provider's own vocabulary, opened out — `drivers_license` is a
               value this platform stores, not a phrase anybody reads. */
            const theirs = docKey === 'documentType' ? documentWords(raw) : raw;
            const tag = verdict?.(c) ?? null;
            /* A typed name or date sits where the service's reading would: say so. */
            const source: IdentitySource =
              docKey === 'fullName'
                ? c.sources.fullName
                : docKey === 'dateOfBirth'
                  ? c.sources.dateOfBirth
                  : 'DOCUMENT';
            return (
              <div
                key={label}
                style={{
                  display: 'grid',
                  /* Stacks on a narrow screen rather than squeezing three columns
                     into a drawer on a laptop. */
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  gap: 'var(--space-1)',
                  paddingBottom: 'var(--space-3)',
                  borderBottom: '1px solid var(--color-divider)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 'var(--space-3)',
                    alignItems: 'center',
                  }}
                >
                  <span className="text-subtle">{label}</span>
                  {tag === null ? null : <Tag tone={tag.tone}>{tag.words}</Tag>}
                </div>
                <div
                  style={{
                    display: 'grid',
                    /* ONE column where there is nothing to compare against. An empty
                       "On the account" cell reads as "the account says nothing",
                       which is a different claim from "this field only exists on a
                       document". */
                    gridTemplateColumns:
                      declared === null
                        ? 'minmax(0, 1fr)'
                        : 'minmax(0, 1fr) minmax(0, 1fr)',
                    columnGap: 'var(--space-4)',
                  }}
                >
                  {/* Each side spans the row's two lines — its label, then its value — on the
                      row's own tracks, so the two values start level even when one label wraps
                      ("Name given at sign-up" on a phone) and the other does not. Read in order,
                      each label is still followed by its own value. */}
                  {declared !== null && (
                    <div style={SIDE_OF_A_ROW}>
                      <div className="text-muted">{accountSideLabel(c, declared)}</div>
                      <AccountSide c={c} field={declared} isName={docKey === 'fullName'} />
                    </div>
                  )}
                  <div style={SIDE_OF_A_ROW}>
                    <div className="text-muted">
                      {documentSideLabel(source, docKey, declared !== null)}
                    </div>
                    <ComparedValue value={theirs} isName={docKey === 'fullName'} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* NOT A COMPARISON, and labelled so nobody reads it as one. Where somebody
            lives and which passport they hold are different facts, and a screen
            that marked them as disagreeing would be wrong about most overseas
            Filipino workers. */}
        <p className="text-subtle" style={{ marginTop: 'var(--space-4)', marginBottom: 0 }}>
          Residence on the account is <strong>{c.declared.residenceCountry ?? '—'}</strong>{' '}
          and the document gives nationality as{' '}
          <strong>{c.document.nationality ?? '—'}</strong>. These are different facts
          and are not compared — somebody may live abroad on a Philippine passport.
        </p>
      </Panel>

      {/* ── THE REST OF WHAT THEY TOLD US ──────────────────────────
             Not comparable with a document, and still part of the decision: item 21
             asks for work, income and employer, and a reviewer judging whether an
             account is what it claims reads them together. */}
      <Panel title="The rest of the profile">
        <dl style={{ display: 'grid', gap: 'var(--space-3)', margin: 0 }}>
          {(
            [
              ['Email', c.declared.email],
              ['Mobile', c.declared.phone],
              [
                'Work',
                (() => {
                  const code = c.declared.natureOfWork;
                  const words = code === null ? null : (WORK_WORDS.get(code) ?? code);
                  return c.declared.natureOfWorkDetail
                    ? `${words ?? '—'} · ${c.declared.natureOfWorkDetail}`
                    : words;
                })(),
              ],
              ['Employer or business', c.declared.employerOrBusiness],
              [
                'Money played with',
                (() => {
                  const code = c.declared.sourceOfIncome;
                  const words = code === null ? null : (INCOME_WORDS.get(code) ?? code);
                  return c.declared.sourceOfIncomeDetail
                    ? `${words ?? '—'} · ${c.declared.sourceOfIncomeDetail}`
                    : words;
                })(),
              ],
              ['Registered', c.declared.registeredOn],
            ] as const
          ).map(([label, value]) => (
            <div
              key={label}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)' }}
            >
              <dt className="text-subtle">{label}</dt>
              <dd style={{ margin: 0, textAlign: 'right', overflowWrap: 'anywhere' }}>
                {value ?? '—'}
              </dd>
            </div>
          ))}
        </dl>
      </Panel>

      {/* ── The photographs, through the one signed link (IdentityPhotos) ──
             A check waiting for a person with none held: it cannot be approved (Verify is closed
             below, and the approve route refuses it), and the player cannot send new ones while a
             person has the check (`eligibility` offers nothing to send). Once it is refused, they
             may send new ones only for a reason they can put right, while they have tries left.
             This said "the player has to send them again", which nothing lets them do. */}
      <IdentityPhotos
        key={c.id}
        recordId={c.id}
        playerRef={c.playerRef}
        imagesHeld={c.imagesHeld}
        empty={
          decidable
            ? 'This check has no photographs, so it cannot be approved. The player cannot send new ' +
              'ones while it waits for a person. Once it is refused, whether they may depends on ' +
              'the reason and the tries they have left.'
            : photosGoneWords(c.imagesPurgedAt)
        }
      />

      {/* ── What the document said ───────────────────────────────── */}
      <Panel title="What the document said">
        <p className="text-subtle" style={{ marginTop: 0 }}>
          {/* `needsName`, not `needsTyping`. A case missing only the date of birth
              has a name, an address and a licence number on the screen below, and
              telling the reviewer nothing was read contradicts what they can see. */}
          {/* And on a check that cannot be decided, where the two fields came from. On a check
              CIRA holds nothing for, approving is not open, so this does not offer it — and it
              says the reading is not on record rather than that nothing was read: CIRA may have
              read a check whose reference the platform could not record. */}
          {!decidable && (c.sources.fullName !== 'DOCUMENT' || c.sources.dateOfBirth !== 'DOCUMENT')
            ? 'What this check holds from the ID. The name and the date of birth say below whether the reviewer typed them rather than the service reading them.'
            : noCiraCheck && needsName
              ? 'No reading of this document is on record.'
              : !decidable && needsName
                ? 'Nothing was read from this document.'
                : needsName
                  ? 'The service could not read this document. Approving it means typing what the ID shows — the name exactly as it is printed.'
                  : 'Read off the ID by the identity service.'}
        </p>
        {/* ── HOW SURE THE SERVICE WAS, ONCE ────────────────────────────
               This line read "with how sure it was of each field", and there was
               no per-field figure to show: CIRA returns ONE confidence for the
               whole document read, so the map was empty and every field rendered
               without a tag. The number is real and worth showing — it is the
               difference between a clean read and a guess — so it is shown where
               the provider actually measured it. */}
        {!needsName && c.document.confidence[DOCUMENT_CONFIDENCE_KEY] !== undefined && (
          <p className="text-subtle" style={{ marginTop: 0, marginBottom: 'var(--space-4)' }}>
            The service rated its own read of this document{' '}
            <Tag
              tone={confidenceTone(c.document.confidence[DOCUMENT_CONFIDENCE_KEY]!)}
            >
              {c.document.confidence[DOCUMENT_CONFIDENCE_KEY]!.toFixed(2)}
            </Tag>
            . That is one figure for the whole card, not per field.
          </p>
        )}
        {needsName ? (
          <EmptyState
            icon="warning"
            title={noCiraCheck ? 'No reading is on record' : 'Nothing was read'}
          >
            {decidable
              ? 'Open the photographs and read the ID yourself.'
              : noCiraCheck
                ? 'No name or date of birth from this document is on record.'
                : 'No name or date of birth was read from this document.'}
          </EmptyState>
        ) : (
          <dl style={{ display: 'grid', gap: 'var(--space-3)', margin: 0 }}>
            {(
              [
                ['Full name', c.document.fullName, 'full_name'],
                ['Date of birth', c.document.dateOfBirth, 'date_of_birth'],
                ['Place of birth', c.document.placeOfBirth, 'place_of_birth'],
                ['Address', c.document.currentAddress, 'address'],
                ['Nationality', c.document.nationality, 'nationality'],
                /* In words, as the comparison above prints it: `drivers_license` is the
                   service's code, not a phrase anybody reads. */
                ['Document', documentWords(c.document.documentType), 'document_type'],
                ['Number', c.document.documentNumber, 'document_number'],
                ['Expires', c.document.documentExpiry, 'expires_on'],
              ] as const
            ).map(([label, value, key]) => {
              /* Per-field, where the provider gives one. CIRA does not — it gives a
                 single figure for the whole read, shown above this list rather than
                 repeated against every row as if it were twelve measurements. */
              const confidence = fieldConfidence(c.document.confidence, key);
              /* Who supplied the two fields an approval can type, where it was not the service. */
              const source =
                label === 'Full name'
                  ? c.sources.fullName
                  : label === 'Date of birth'
                    ? c.sources.dateOfBirth
                    : 'DOCUMENT';
              const typed = source === 'DOCUMENT' ? null : sourceWords(value, source, c);
              return (
                <div
                  key={label}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)' }}
                >
                  <dt className="text-subtle">{label}</dt>
                  <dd style={{ margin: 0, textAlign: 'right' }}>
                    {value ?? '—'}
                    {confidence !== undefined && (
                      <>
                        {' '}
                        <Tag tone={confidenceTone(confidence)}>{confidence.toFixed(2)}</Tag>
                      </>
                    )}
                    {typed !== null && <div className="text-subtle">{typed}</div>}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}

        {/*
          * THE COMPARISON IS THE REVIEW. A date of birth the player typed at
          * registration and one read off their passport are two claims about
          * one fact; a reviewer should not have to open two pages to see that
          * they disagree.
          */}
        {c.declared.dateOfBirth && (
          <p style={{ marginTop: 'var(--space-4)' }} className={dobDisagrees ? '' : 'text-subtle'}>
            {dobDisagrees ? (
              /* A sentence in a tag: it wraps on a phone rather than running off the drawer, and
                 each date stays whole — WebKit broke one at its hyphen. */
              <Tag tone="accent" className="tag-wraps">
                They disagree — the player said{' '}
                <span style={{ whiteSpace: 'nowrap' }}>{c.declared.dateOfBirth}</span>, the ID says{' '}
                <span style={{ whiteSpace: 'nowrap' }}>{c.document.dateOfBirth}</span>
              </Tag>
            ) : c.declared.correction?.dateOfBirthChanged === true ? (
              /* The first entry, kept, beside what the player says now. */
              c.declared.correction.signUpDateOfBirth === null ? (
                <>
                  After a refusal the player gave their date of birth as {c.declared.dateOfBirth};
                  none was recorded at registration.
                </>
              ) : (
                <>
                  After a refusal the player corrected their date of birth to{' '}
                  {c.declared.dateOfBirth}; at registration they said{' '}
                  {c.declared.correction.signUpDateOfBirth}.
                </>
              )
            ) : (
              <>At registration the player said {c.declared.dateOfBirth}.</>
            )}
          </p>
        )}
        {/* A CORRECTION MADE AFTER THIS CHECK, said as that: the sentence above is about the date the
            check was sent. */}
        {laterDateOfBirth !== null && laterCorrection !== null && (
          <p className="text-subtle" style={{ marginTop: 'var(--space-2)', marginBottom: 0 }}>
            After this check, on {calendarDay(laterCorrection.correctedOn)}, the player corrected their
            date of birth to <span style={{ whiteSpace: 'nowrap' }}>{laterDateOfBirth}</span>.
          </p>
        )}
      </Panel>

      {/* No `allowed('kyc.review')` here any more: the screen itself now
          refuses above, so a reader reaching this line knows the
          capability is held. A second check would imply it might not be,
          and that is how a reviewer ends up wondering which one is
          authoritative. */}
      {/* ONLY WHERE A VERDICT CAN BE GIVEN: a current check waiting for a person. A
          decided check opens read-only, and so does a waiting one that a newer check
          replaced — the verdict route would refuse either. */}
      {decidable ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button onClick={onVerify} disabled={busy || c.imagesHeld.length === 0 || noCiraCheck}>
              <Icon name="check" /> Verify this player
            </Button>
            <Button variant="secondary" onClick={onRefuse} disabled={busy}>
              <Icon name="x" /> Refuse
            </Button>
          </div>
          {noCiraCheck && (
            <p className="text-subtle" style={{ margin: 0 }}>
              This check cannot be approved: no CIRA check is on record for these documents.
            </p>
          )}
        </div>
      ) : c.oneMoreTry?.canAllow === true ? (
        /* ON A CLOSED REFUSAL, where the case route says the press would be taken: the player's
           current check, refused for a final reason or with the tries spent, and nothing allowed
           after it yet. The screen itself is behind kyc.review, the press's own key. */
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <p className="text-subtle" style={{ margin: 0 }}>
            {whyTheCaseIsClosed(c.refusalsSoFar)} Support can allow one more try, with a reason.
          </p>
          <div>
            <Button variant="secondary" onClick={onAllowOneMoreTry} disabled={busy}>
              <Icon name="clock-counter-clockwise" /> {ALLOW_ONE_MORE_TRY}
            </Button>
          </div>
        </div>
      ) : c.outcome === 'REFUSED' && c.oneMoreTry?.whyNot ? (
        /* A REFUSAL NO GRANT CAN ANSWER NOW — tries of the player's own, a newer check since, one
           already allowed, no reason on record, or the account suspended: the press's own
           sentence, so the missing button says why. */
        <p className="text-subtle" style={{ margin: 0 }}>
          {c.oneMoreTry.whyNot}
        </p>
      ) : c.decision === null ? (
        <p className="text-subtle" style={{ margin: 0 }}>
          Nothing to decide here:{' '}
          {c.current ? 'this check is not waiting for a person.' : 'a newer check replaced this one.'}
        </p>
      ) : null}
    </div>
  );
}
